/* 7 Card Tarot V2 engine — faithful runtime for the user's v2 design markup.
   Markup carries the design's exact inline styles; this engine fills the
   data-rv2-* binding points with the design's own computed formulas, draws
   from the shared 78-card deck, fetches the dedicated /api/seven-card
   free reading (audited recognition overview + preview token)
   and authorizes the proven deep-reading checkout rail. */
(function () {
  'use strict';
  if (!document.querySelector('[data-sc7]')) return;

  var PAGE = '/pages/7-card-tarot-reading';
  var API_BASE = 'https://reading.deckaura.com/api/seven-card';
  var RAIL_TIERS = ['deeper', 'indepth'];
  var ANALYTICS_SCHEMA = 'sc7_funnel_v1';
  var FUNNEL_VERSION = 'seven-card-two-tier-2026-09-04-v1';
  var PRESENTATION_VARIANT = 'seven-card-compact-v1';
  var MIN_QUESTION_LENGTH = 8;
  var RESULT_MODES = ['success', 'fallback', 'limit', 'validation_error', 'network_error', 'server_error', 'timeout'];
  var CANONICAL = ['Past', 'Present', 'Hidden Influences', 'Obstacle', 'External Influences', 'Advice', 'Likely Outcome'];
  var DISPLAY = CANONICAL.slice();
  var SHORTS = { 'The High Priestess': 'High Priestess', 'Wheel of Fortune': 'Wheel of Fortune' };
  var LIFTS = [26, 11, 2, -2, 2, 11, 26];
  var SCOPE = 'Seven-card Horseshoe overview linking past, present, hidden influences, obstacle, external influences, advice, and a conditional likely outcome.';
  var CONFIDENCE = 'Symbolic reflection only; the likely outcome is conditional and choices can change the path.';
  var CONTEXT = 'Seven-card Horseshoe arc. Read Past into Present; connect Hidden Influences, Obstacle and External Influences; treat Advice as the action within the customer\'s control and Likely Outcome as conditional rather than fixed.';

  var EXAMPLE_CARDS = [
    { label: 'Past', name: 'Six of Cups', short: 'Six of Cups', line: 'An earlier version of this bond still shapes how you read the present.' },
    { label: 'Present', name: 'Two of Swords', short: 'Two of Swords', line: 'Right now the situation is held in place by a decision no one has made.' },
    { label: 'Hidden influence', name: 'The High Priestess', short: 'High Priestess', line: 'Something important may still be unspoken or not fully understood.' },
    { label: 'Obstacle', name: 'Knight of Wands', short: 'Knight of Wands', line: 'Momentum starts and stops. Effort arrives in bursts, then withdraws.', reversed: true },
    { label: 'External influence', name: 'Three of Swords', short: 'Three of Swords', line: 'An outside opinion, or an older hurt, is being carried into this.' },
    { label: 'Advice', name: 'Temperance', short: 'Temperance', line: 'Move at a measured pace and let one clear exchange do the work.' },
    { label: 'Outcome', name: 'Wheel of Fortune', short: 'Wheel of Fortune', line: 'The direction turns on whether the current pattern changes.' }
  ];
  var LOCKED = [
    { title: 'Current path versus changed path', pkg: 'The Two Paths', body: 'See where the situation may lead if the current pattern continues, and what may change if you act.' },
    { title: 'How this applies to your exact situation', pkg: 'The Answer', body: 'Your seven cards read against the question you asked, not a general spread.' },
    { title: 'The first sign that the path is shifting', pkg: 'The Two Paths', body: 'What to watch for over the next few weeks, and what it would mean.' }
  ];
  var NEED_LABELS = [
    'Help me understand what the cards mean together.',
    'Show me what may happen if I act, wait or change direction.',
    'Help me create a plan for what to do next.'
  ];
  var POSITIONS_INFO = [
    ['01', 'Past', 'How the situation formed and what it is still carrying.'],
    ['02', 'Present', 'Where things actually stand today, not where they feel stuck.'],
    ['03', 'Hidden influence', 'What is shaping the situation without being said out loud.'],
    ['04', 'Obstacle', 'The friction that keeps interrupting progress.'],
    ['05', 'External influence', 'People, timing or circumstances outside your control.'],
    ['06', 'Advice', 'The approach the spread points toward.'],
    ['07', 'Outcome', 'Where the current direction leads if the pattern holds.']
  ];
  var SAMPLES = [
    'Should I reach out to him, or wait?',
    'What happens if I stay in this role another year?',
    'What keeps repeating in my relationships, and what could interrupt it?'
  ];
  var FAQ_DEFS = [
    ['What exactly will I receive?', 'A written reading by email, plus a private page you can return to. It contains your question, your seven cards, the full interpretation for the level you chose, and the practical next steps.'],
    ['What is the difference between the free overview and a paid reading?', 'The free reading outlines the card pattern. An optional personalized written reading uses the same question and cards to explore the deciding condition, different timing possibilities and practical next steps at your chosen depth.'],
    ['Who reviews the paid reading?', "Your interpretation is prepared with AI and traditional tarot symbolism from your question, context and cards, with automated quality checks. This supports reflection and does not guarantee outcomes or reveal private thoughts."],
    ['How long does delivery take?', ''],
    ['What if I enter the wrong email address?', 'Reply to your order confirmation, or use the contact link in the footer. We resend to the corrected address at no extra cost.'],
    ['Can I ask about another person?', 'You can ask about a situation that involves someone else. The reading describes the dynamic as your cards reflect it. It does not claim access to another person’s private thoughts.'],
    ['Are reversed cards included?', 'Yes. Reversed cards are read as modifiers of their position, usually pointing to something delayed, internal, or working differently than expected.'],
    ['Can I change my question after checkout?', 'You can add context or correct the wording within the first 30 minutes by replying to your confirmation email, as long as the reading has not been sent.'],
    ['Is this a subscription?', 'No. Every reading is a one-time purchase. Nothing is stored for future billing.'],
    ['What is the refund policy?', 'If a reading has not been prepared yet, it is fully refundable. Once a personalized reading has been written and delivered, it cannot be refunded, because it is a one-off written product.'],
    ['How is my question stored, and can I delete it?', 'Your question is stored with your reading only, is never sent to analytics, and is not published. You can request deletion at any time and it is removed within seven days.']
  ];

  /* ------------ state ------------ */
  var s = {
    question: '', intent: '', phase: 'intro', mode: 'live', revealed: 0, active: -1,
    focused: false, need: 0, pkg: 0, compare: false, cardsOpen: false, faq: -1,
    wide: window.innerWidth > 900, xwide: window.innerWidth > 1150, inPkg: false,
    qError: false, deckStatus: 'idle', live: '', cards: [], server: null, readingId: '', busy: false,
    previewToken: '', freeReadings: null, railReadingId: '', railPreviewToken: '',
    checkoutQueued: false, checkoutVia: '', checkoutBusy: false,
    sessionId: '', attemptId: '', sentEvents: {}, questionStarted: false,
    pricing: { status: 'idle', currency: '', latency_ms: 0, error_code: '', tiers: [] }
  };
  var SESSION_KEY = 'deckaura_sc7_v2_state_v3';
  var SESSION_ID_KEY = 'deckaura_sc7_funnel_v1_session_id';
  var FREE_PREVIEW_LIMIT_KEY = 'deckaura_sc7_free_preview_limit_v2';
  var FREE_PREVIEW_LIMIT_MAX_FUTURE = 25 * 60 * 60 * 1000;
  var FREE_PREVIEW_LIMIT_REASONS = {
    visitor_rate_limit: 1,
    device_rate_limit: 1,
    network_rate_limit: 1,
    global_daily_limit: 1
  };
  var SESSION_MAX_AGE = 2 * 60 * 60 * 1000;
  var milestoneObserver = null;
  var seenMilestones = {};
  var visibilityTimers = {};
  var engagedVisible = false;
  var engagedLastTick = 0;
  var engagedMs = 0;
  var engagedTimer = 0;
  var priceWatchdog = 0;
  var checkoutWatchdog = 0;
  var railReadyTimer = 0;
  var railReadyAttempts = 0;
  var packageFocusIndex = -1;
  var freeRequestId = 0;
  var freeRequestController = null;
  var deckAttemptId = 0;
  var dependencyLoads = {};

  function invalidateFreeRequest() {
    freeRequestId += 1;
    if (freeRequestController) freeRequestController.abort();
    freeRequestController = null;
    s.busy = false;
  }

  // A failed deferred script is not repaired by retrying its missing API.
  // Share each in-flight download; discard rejected attempts so retry can load it again.
  function ensureDependency(fileName, ready, retry) {
    if (ready()) return Promise.resolve();
    if (dependencyLoads[fileName]) return dependencyLoads[fileName];
    var task = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src*="/' + fileName + '"]');
      var source = existing && existing.src;
      if (!source) { reject(new Error('dependency_source_missing')); return; }
      var script = existing;
      var create = retry || document.readyState === 'complete';
      if (create) {
        script = document.createElement('script');
        var retryUrl = new URL(source, window.location.href);
        retryUrl.searchParams.set('_deckaura_retry', Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8));
        script.src = retryUrl.href;
        script.async = true;
        existing.remove();
      }
      var settled = false;
      var timer = 0;
      var poll = 0;
      function finish(error) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        window.clearInterval(poll);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
        if (error) reject(error); else resolve();
      }
      function loaded() { finish(ready() ? null : new Error('dependency_api_missing')); }
      function failed() { finish(new Error('dependency_download_failed')); }
      script.addEventListener('load', loaded);
      script.addEventListener('error', failed);
      poll = window.setInterval(function () { if (ready()) finish(); }, 100);
      timer = window.setTimeout(function () { finish(new Error('dependency_timeout')); }, 8000);
      if (create) document.head.appendChild(script);
    });
    dependencyLoads[fileName] = task;
    function release() { if (dependencyLoads[fileName] === task) delete dependencyLoads[fileName]; }
    task.then(release, release);
    return task;
  }
  function checkoutRuntimeReady() {
    return !!(window.DeckauraDeepReading && typeof window.DeckauraDeepReading.enable === 'function');
  }
  function loadCheckoutRuntime(retry) {
    return ensureDependency('deep-reading-cta-runtime.js', checkoutRuntimeReady, retry);
  }

  function safeId(value) { return /^[a-zA-Z0-9_-]{12,96}$/.test(String(value || '')); }
  function uuid(prefix) {
    try {
      if (crypto && typeof crypto.randomUUID === 'function') return (prefix || '') + crypto.randomUUID();
      var bytes = new Uint8Array(16); crypto.getRandomValues(bytes);
      return (prefix || '') + Array.prototype.map.call(bytes, function (x) { return x.toString(16).padStart(2, '0'); }).join('');
    } catch (_) { return (prefix || '') + Date.now().toString(36) + Math.random().toString(36).slice(2, 14); }
  }
  function sessionId() {
    try {
      var existing = sessionStorage.getItem(SESSION_ID_KEY);
      if (safeId(existing)) return existing;
      var next = uuid('s_');
      sessionStorage.setItem(SESSION_ID_KEY, next);
      return next;
    } catch (_) { return uuid('s_'); }
  }
  function cleanToken(value, max) {
    return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9_:-]/g, '_').slice(0, max || 80);
  }
  function safeEventData(data) {
    var allowed = {
      source: 1, intent: 1, tier: 1, via: 1, status: 1, currency: 1,
      latency_ms: 1, error_code: 1, variant_id: 1, price_minor: 1,
      available: 1, reason: 1, result_mode: 1, card_count: 1,
      revealed_count: 1, orientation: 1, destination: 1
    };
    var out = {};
    Object.keys(data || {}).forEach(function (key) {
      if (!allowed[key]) return;
      var value = data[key];
      if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
      else if (typeof value === 'boolean') out[key] = value;
      else if (value != null) out[key] = key === 'currency'
        ? String(value).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)
        : cleanToken(value, key === 'variant_id' ? 24 : 80);
    });
    return out;
  }
  function publishFunnelEvent(name, data, options) {
    if (analyticsSuppressed()) return false;
    if (!/^[a-z][a-z0-9_]{2,64}$/.test(String(name || ''))) return false;
    if (name === 'free_overview_resolved' && RESULT_MODES.indexOf(String(data && data.result_mode || '')) < 0) {
      data = Object.assign({}, data || {}, { result_mode: 'server_error' });
    }
    var once = options && options.once === true;
    var scope = s.attemptId || s.sessionId || 'page';
    var dedupeKey = scope + ':' + name;
    if (name === 'free_overview_resolved') {
      var mode = String(data && data.result_mode || '');
      dedupeKey += ':' + (/^(success|fallback)$/.test(mode) ? 'delivered' : mode === 'limit' ? 'visitor_limit' : 'error');
    }
    if (once && s.sentEvents[dedupeKey]) return false;
    var payload = Object.assign({
      analytics_schema: ANALYTICS_SCHEMA,
      funnel_version: FUNNEL_VERSION,
      session_id: s.sessionId,
      attempt_id: s.attemptId || '',
      reading_id: s.readingId || '',
      event_uuid: uuid('e_'),
      occurred_at: new Date().toISOString()
    }, safeEventData(data));
    if (once) {
      s.sentEvents[dedupeKey] = payload.event_uuid;
      persistState();
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (_) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', name, payload); } catch (_) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (_) {}
    try { document.dispatchEvent(new CustomEvent('deckaura:sc7-funnel-event', { detail: { name: name, payload: payload } })); } catch (_) {}
    return true;
  }
  function analyticsSuppressed() {
    try {
      var audit = new URLSearchParams(window.location.search || '');
      var internal = audit.has('funnel_audit') || audit.get('internal') === 'true' || audit.get('traffic_type') === 'internal'
        || audit.has('deckaura_internal') || audit.has('codex_audit') || audit.has('alltools_audit');
      if (internal) {
        try { sessionStorage.setItem('deckaura_sc7_internal_audit_v1', '1'); } catch (_) {}
        return true;
      }
      try { return sessionStorage.getItem('deckaura_sc7_internal_audit_v1') === '1'; } catch (_) { return false; }
    } catch (_) { return false; }
  }
  function ensureAttempt(source) {
    if (s.attemptId) return s.attemptId;
    s.attemptId = uuid('a_');
    s.sentEvents = {};
    publishFunnelEvent('first_interaction', { source: source || 'unknown' }, { once: true });
    persistState();
    return s.attemptId;
  }

  function persistState() {
    if (s.mode !== 'live') return;
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        v: 4,
        at: Date.now(),
        state: {
          question: s.question, intent: s.intent, phase: s.phase,
          revealed: s.revealed, active: s.active, need: s.need, pkg: s.pkg,
          compare: s.compare, cardsOpen: s.cardsOpen, cards: s.cards,
          server: s.server, readingId: s.readingId,
          previewToken: s.previewToken, freeReadings: s.freeReadings,
          sessionId: s.sessionId, attemptId: s.attemptId,
          sentEvents: s.sentEvents, questionStarted: s.questionStarted,
          pricing: s.pricing
        }
      }));
    } catch (_) {}
  }

  function restoreState() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
      if (!saved || saved.v !== 4 || !saved.at || Date.now() - saved.at > SESSION_MAX_AGE || !saved.state) {
        sessionStorage.removeItem(SESSION_KEY);
        return false;
      }
      var next = saved.state;
      var cards = Array.isArray(next.cards) ? next.cards.filter(function (item) {
        return item && item.card && typeof item.card.name === 'string';
      }).slice(0, 7) : [];
      var phase = ['intro', 'preparing', 'reveal', 'result'].indexOf(next.phase) >= 0 ? next.phase : 'intro';
      if (phase !== 'intro' && cards.length !== 7) { phase = 'intro'; cards = []; } // Keep an unfinished question after a failed download.
      if (phase === 'preparing') phase = 'reveal';
      s.question = String(next.question || '').slice(0, 240);
      s.intent = ['', 'Love & connection', 'A decision', 'Career', 'Personal growth'].indexOf(next.intent) >= 0 ? next.intent : '';
      s.phase = phase;
      s.mode = 'live';
      s.cards = cards;
      s.revealed = cards.length === 7 ? Math.max(phase === 'result' ? 7 : 0, Math.min(7, Number(next.revealed) || 0)) : 0;
      s.active = cards.length === 7 ? Math.max(-1, Math.min(6, Number.isInteger(next.active) ? next.active : s.revealed - 1)) : -1;
      s.need = Number.isInteger(next.need) && next.need >= 0 && next.need < RAIL_TIERS.length ? next.need : 0;
      s.pkg = Number.isInteger(next.pkg) && next.pkg >= 0 && next.pkg < RAIL_TIERS.length ? next.pkg : s.need;
      s.compare = next.compare === true;
      s.cardsOpen = next.cardsOpen === true;
      s.readingId = cards.length === 7 ? String(next.readingId || '').slice(0, 80) : '';
      s.previewToken = cards.length === 7 && /^[a-f0-9]{32}$/i.test(String(next.previewToken || '')) ? String(next.previewToken) : '';
      s.server = next.server && typeof next.server === 'object' && s.previewToken ? next.server : null;
      s.freeReadings = next.freeReadings && typeof next.freeReadings === 'object' ? next.freeReadings : null;
      s.sessionId = safeId(next.sessionId) ? next.sessionId : s.sessionId;
      s.attemptId = safeId(next.attemptId) ? next.attemptId : '';
      s.sentEvents = next.sentEvents && typeof next.sentEvents === 'object' ? next.sentEvents : {};
      s.questionStarted = next.questionStarted === true;
      if (next.pricing && /^(idle|loading|ready|error)$/.test(String(next.pricing.status || ''))) s.pricing = next.pricing;
      s.qError = false; s.busy = false; s.checkoutBusy = false; s.checkoutQueued = false;
      s.wide = window.innerWidth > 900; s.xwide = window.innerWidth > 1150;
      return true;
    } catch (_) {
      try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
      return false;
    }
  }

  function deck() {
    var d = window.DeckauraTarotCards;
    return Array.isArray(d) && d.length === 78 ? d : null;
  }
  function localeContext() {
    var cfg = window.DeckauraLocale && typeof window.DeckauraLocale === 'object' ? window.DeckauraLocale : {};
    var shopify = window.Shopify && typeof window.Shopify === 'object' ? window.Shopify : {};
    var locale = String(cfg.locale || shopify.locale || document.documentElement.lang || 'en').replace('_', '-').toLowerCase();
    var lang = locale.indexOf('pt-br') === 0 ? 'ptbr' : locale.split('-')[0];
    if (['en', 'tr', 'de', 'es', 'pt', 'ptbr'].indexOf(lang) < 0) lang = 'en';
    var currency = String(cfg.currency || (shopify.currency && shopify.currency.active) || '').toUpperCase();
    var country = String(cfg.country || cfg.countryCode || cfg.country_code || shopify.country || '').toUpperCase();
    return { lang: lang, locale: locale, country: country, currency: currency };
  }
  var COPY = {
    en: {
      deliveryLong: "AI-assisted · most within 90 min", deliveryShort: "AI-assisted · most within 90 min",
      intents: ['Love & connection', 'A decision', 'Career', 'Personal growth'],
      needs: ['Help me understand what the cards mean together.', 'Show me what may happen if I act, wait or change direction.', 'Help me create a plan for what to do next.'],
      tierNames: ['Essential Personalized Answer', 'Focused Personalized Answer', 'In-Depth Personalized Reading'],
      priceLoading: 'Checking secure price…', priceError: 'Price unavailable', packageUnavailable: 'This package is temporarily unavailable', retryPrice: 'Retry secure price check',
      freeHeading: 'What your seven cards show together', freeBridge: 'The free reading outlines the card pattern. An optional personalized written reading uses the same question and cards to explore the deciding condition, different timing possibilities and practical next steps at your chosen depth.',
      resultLimit: 'The free enhanced-preview allowance for the current rolling 24-hour window has been reached. Your seven cards and secure checkout remain available.',
      resultFallback: 'Your seven cards and connected overview are ready below.',
      revealAll: 'Reveal all 7 cards & show my free overview', showOverview: 'Show my free overview', openNext: 'Open next card',
      questionReady: 'Your question is ready to draw', writeQuestion: 'Write one focused question', drawCards: 'Draw 7 Cards', drawAgain: 'Draw a new spread',
      startReading: 'Start my free reading', openingCheckout: 'Opening secure checkout…', preparingReading: 'Preparing your secure reading…', chooseTier: 'Get ',
      pricingReady: 'Secure market price confirmed', pricingLoading: 'Confirming the secure price for your market…'
    },
    ptbr: {
      deliveryLong: "Preparada com IA · a maioria em 90 min", deliveryShort: "Preparada com IA · a maioria em 90 min",
      intents: ['Amor e conexão', 'Uma decisão', 'Carreira', 'Crescimento pessoal'],
      needs: ['Quero entender o significado das cartas em conjunto.', 'Quero comparar o que pode acontecer se eu agir, esperar ou mudar de direção.', 'Quero um plano claro para o que fazer a seguir.'],
      tierNames: ['Essencial', 'Focada', 'Completa'],
      priceLoading: 'Verificando preço seguro…', priceError: 'Preço indisponível', packageUnavailable: 'Este pacote está temporariamente indisponível', retryPrice: 'Tentar verificar o preço novamente',
      freeHeading: 'O que suas sete cartas mostram em conjunto', freeBridge: 'A leitura gratuita descreve o padrão das cartas. Uma leitura escrita opcional usa a mesma pergunta e cartas para explorar a condição decisiva, diferentes possibilidades de ritmo e próximos passos práticos na profundidade escolhida.',
      resultLimit: 'O limite de prévias personalizadas gratuitas da janela móvel de 24 horas foi atingido. Suas sete cartas e o checkout seguro continuam disponíveis.',
      resultFallback: 'Suas sete cartas e a visão conectada estão prontas abaixo.',
      revealAll: 'Revelar as 7 cartas e ver minha visão gratuita', showOverview: 'Ver minha visão gratuita', openNext: 'Abrir próxima carta',
      questionReady: 'Sua pergunta está pronta', writeQuestion: 'Escreva uma pergunta objetiva', drawCards: 'Tirar 7 cartas', drawAgain: 'Tirar uma nova sequência',
      startReading: 'Começar minha leitura gratuita', openingCheckout: 'Abrindo checkout seguro…', preparingReading: 'Preparando sua leitura segura…', chooseTier: 'Escolher ',
      pricingReady: 'Preço seguro confirmado para seu mercado', pricingLoading: 'Confirmando o preço seguro para seu mercado…'
    },
    tr: {
      deliveryLong: "Yapay zekâ desteğiyle hazırlanır · çoğu 90 dakika içinde", deliveryShort: "Yapay zekâ desteğiyle hazırlanır · çoğu 90 dakika içinde",
      intents: ['Aşk ve bağ', 'Bir karar', 'Kariyer', 'Kişisel gelişim'], tierNames: ['Temel', 'Odaklı', 'Kapsamlı'],
      priceLoading: 'Güvenli fiyat kontrol ediliyor…', priceError: 'Fiyat kullanılamıyor', packageUnavailable: 'Bu paket geçici olarak kullanılamıyor', retryPrice: 'Güvenli fiyat kontrolünü yeniden dene',
      freeHeading: 'Yedi kartınız birlikte ne gösteriyor?', freeBridge: 'Ücretsiz yorum kartların örüntüsünü gösterir. İsteğe bağlı, kişisel yazılı yorum aynı soru ve kartlarla belirleyici koşulu, farklı zamanlama olasılıklarını ve seçtiğiniz kapsamda uygulanabilir sonraki adımları inceler.',
      resultLimit: 'Son 24 saatlik hareketli dönem için ücretsiz kişiselleştirilmiş önizleme hakkı doldu. Yedi kartınız ve güvenli ödeme seçeneği kullanılabilir durumda.',
      resultFallback: 'Yedi kartınız ve bağlantılı genel bakış aşağıda hazır.',
      revealAll: '7 kartın tümünü aç ve ücretsiz genel bakışımı göster', showOverview: 'Ücretsiz genel bakışımı göster', openNext: 'Sonraki kartı aç',
      questionReady: 'Sorunuz kart çekmeye hazır', writeQuestion: 'Tek bir odaklı soru yazın', drawCards: '7 Kart Çek', drawAgain: 'Yeni bir açılım yap',
      startReading: 'Ücretsiz yorumuma başla', openingCheckout: 'Güvenli ödeme açılıyor…', preparingReading: 'Güvenli yorumunuz hazırlanıyor…', chooseTier: 'Seç: ',
      pricingReady: 'Pazarınıza ait güvenli fiyat doğrulandı', pricingLoading: 'Pazarınıza ait güvenli fiyat doğrulanıyor…'
    }
  };

  Object.assign(COPY.en, {
    positions: ['Past', 'Present', 'Hidden Influences', 'Obstacle', 'External Influences', 'Advice', 'Likely Outcome'],
    positionInfo: [
      ['01', 'Past', 'How the situation formed and what it is still carrying.'],
      ['02', 'Present', 'Where things actually stand today.'],
      ['03', 'Hidden Influences', 'What is shaping the situation beneath the surface.'],
      ['04', 'Obstacle', 'The friction that keeps interrupting progress.'],
      ['05', 'External Influences', 'People, timing or conditions outside your control.'],
      ['06', 'Advice', 'The response that remains within your control.'],
      ['07', 'Likely Outcome', 'Where the current direction may lead if the pattern holds.']
    ],
    samples: ['Should I reach out, or wait?', 'What happens if I stay in this role another year?', 'What keeps repeating in my relationships, and what could interrupt it?'],
    packages: [
      { badge: 'Understand the spread', length: '450–600 words', promise: 'Turn your seven cards into one clear conclusion.', bullets: ['The complete story across all 7 cards', 'Your strongest signal and central tension', 'The condition shaping the outcome', 'One grounded next step'] },
      { badge: 'Compare two directions', length: '750–950 words', promise: 'Compare the current path with what may change if you act, wait or change direction.', bullets: ['Everything in Essential Personalized Answer', 'Current path versus changed path', 'What strengthens or weakens the outcome', 'Three practical next steps'] },
      { badge: 'Full guidance', length: '1,250–1,500 words', promise: 'Add clarifying cards and turn the reading into a practical 30-day plan.', bullets: ['Everything in Focused Personalized Answer', 'A targeted 3-card clarification spread', 'Risks, safeguards and boundaries', 'A week-by-week 30-day action plan'] }
    ],
    recommended: 'Start with Focused', selected: 'Selected', selectedLevel: 'Your selected reading level',
    compareShow: 'Compare all options', compareHide: 'Hide options',
    selectedReason: 'You selected this level. Your question, focus and seven cards stay attached through secure checkout.',
    reasonEssential: 'Your question is framed for a direct yes/no-style answer, so the Essential level is the closest fit.',
    reasonCompare: 'Your question compares two possible directions, so the Focused level reads both paths side by side.',
    reasonPlan: 'Your question asks how to act or what to do next, so the In-Depth level adds clarifying cards and a step-by-step plan.',
    arc1: 'For “{question}”, {pastName} in Past brings {pastMeaning}, while {presentName} in Present brings {presentMeaning}; together they show how the earlier pattern reaches the situation now.',
    arc2: 'Beneath the surface, {hiddenName} points to {hiddenMeaning}; {obstacleName} makes {obstacleMeaning} the obstacle, while {externalName} adds {externalMeaning} from outside.',
    arc3: '{adviceName} advises {adviceMeaning}; if that guidance is applied, {outcomeName} describes a conditional direction of {outcomeMeaning}, not a fixed verdict.',
    reversed: 'Reversed', upright: 'Upright', card: 'Card', faceDown: 'face down. Open it.',
    hintOne: 'One focused situation works best.', hintMany: 'Try focusing on one situation rather than several unrelated questions.', hintFocused: '✓ Focused question',
    tableReady: 'Your question is ready. Draw when you are.', tableWrite: 'Write your question to set the reading.',
    activeEmpty: 'Your spread is ready to open', activeEmptyLine: 'Open one card at a time so each position has room before the full story is connected.',
    activeFrame: 'This card is symbolic, not a fixed prediction. In this position it points to a pattern to notice, question or change.',
    progress: '{n} of 7 cards revealed', cardRevealed: 'Card {n} of 7 revealed.', allRevealed: 'All seven cards revealed.',
    step3: 'Step 3 of 3 · Your reading', viewOptions: 'View options', step1Ask: 'Step 1 of 3 · Ask', step1Ready: 'Step 1 of 3 · Question ready',
    step2Preparing: 'Step 2 of 3 · Preparing', arranging: 'Arranging your seven-card spread', preparing: 'Preparing…', step2Card: 'Step 2 of 3 · Card {n} of 7', allOpen: 'All seven cards are open',
    sampleStep: 'Sample reading · Step 3', sampleFlow: 'See the flow, then draw your own', startMine: 'Start my reading',
    teaserCta: 'Unlock the deciding condition', visibleLabel: 'What is visible', visibleLead: 'The story begins with Past and Present.',
    finalExampleHeading: 'Ready to draw your own seven cards?', finalExampleBody: 'Ask one focused question and receive your own Horseshoe spread.',
    finalHeading: 'Your seven cards are already here.', finalBody: 'Return to your reading and choose the level of guidance you need.', finalCta: 'Return to my reading',
    checkoutFallback: 'Your free overview service is unavailable right now, but your cards, on-page overview and secure checkout remain available.',
    quotaNote: 'Free card draws and the on-page overview remain available. The enhanced preview service is limited to 3 requests per rolling 24 hours across Deckaura tools.',
    checkoutReady: 'Secure checkout is ready. Your question, focus and seven cards will stay attached.',
    checkoutRetry: 'Checkout did not open. Your reading is safe; please try again.',
    priceRetrying: 'Retrying the secure price check…', priceConfirming: 'Confirming the secure price for your market…', checkoutOpening: 'Opening secure checkout…',
    readingIncomplete: 'Complete your free reading before choosing a package.', packageFailed: 'This package could not be prepared. Please choose it again or try another level.',
    faq: FAQ_DEFS
  });

  COPY.es = {
    deliveryLong: "Preparada con IA · la mayoría en 90 min", deliveryShort: "Preparada con IA · la mayoría en 90 min",
    intents: ['Amor y conexión', 'Una decisión', 'Carrera', 'Crecimiento personal'],
    needs: ['Ayúdame a entender qué significan juntas las cartas.', 'Muéstrame qué puede pasar si actúo, espero o cambio de rumbo.', 'Ayúdame a crear un plan para lo que debo hacer después.'],
    tierNames: ['Respuesta Personalizada Esencial', 'Respuesta Personalizada Enfocada', 'Lectura Personalizada Completa'],
    priceLoading: 'Comprobando el precio seguro…', priceError: 'Precio no disponible', packageUnavailable: 'Este paquete no está disponible temporalmente', retryPrice: 'Reintentar la comprobación segura del precio',
    freeHeading: 'Lo que muestran juntas tus siete cartas', freeBridge: 'Esta síntesis conecta las siete posiciones con los significados reales de tus cartas. La lectura personal de pago es opcional y aplica el patrón a tu situación exacta.',
    resultLimit: 'Se alcanzó el límite de vistas previas gratuitas de la ventana móvil de 24 horas. Tus cartas y el pago seguro siguen disponibles.', resultFallback: 'Tus siete cartas y su síntesis están listas.',
    revealAll: 'Revelar las 7 cartas y ver mi síntesis gratuita', showOverview: 'Ver mi síntesis gratuita', openNext: 'Abrir la siguiente carta',
    questionReady: 'Tu pregunta está lista', writeQuestion: 'Escribe una pregunta concreta', drawCards: 'Sacar 7 cartas', drawAgain: 'Sacar una nueva tirada',
    startReading: 'Empezar mi lectura gratuita', openingCheckout: 'Abriendo el pago seguro…', preparingReading: 'Preparando tu lectura segura…', chooseTier: 'Obtener ',
    pricingReady: 'Precio seguro confirmado para tu mercado', pricingLoading: 'Confirmando el precio seguro para tu mercado…',
    positions: ['Pasado', 'Presente', 'Influencias ocultas', 'Obstáculo', 'Influencias externas', 'Consejo', 'Resultado probable'],
    positionInfo: [['01','Pasado','Cómo se formó la situación y qué sigue arrastrando.'],['02','Presente','Dónde se encuentra realmente la situación hoy.'],['03','Influencias ocultas','Qué actúa bajo la superficie.'],['04','Obstáculo','La fricción que interrumpe el avance.'],['05','Influencias externas','Personas, tiempos o condiciones fuera de tu control.'],['06','Consejo','La respuesta que sí está en tus manos.'],['07','Resultado probable','Adónde puede llevar la dirección actual si el patrón continúa.']],
    samples: ['¿Debería contactar o esperar?', '¿Qué pasa si sigo otro año en este puesto?', '¿Qué se repite en mis relaciones y qué podría interrumpirlo?'],
    packages: [
      {badge:'Entender la tirada',length:'450–600 palabras',promise:'Convierte tus siete cartas en una conclusión clara.',bullets:['La historia completa de las 7 cartas','Tu señal más fuerte y la tensión central','La condición que moldea el resultado','Un siguiente paso realista']},
      {badge:'Comparar dos caminos',length:'750–950 palabras',promise:'Compara el camino actual con lo que puede cambiar si actúas, esperas o cambias de rumbo.',bullets:['Todo lo incluido en Esencial','Camino actual frente a camino cambiado','Qué fortalece o debilita el resultado','Tres próximos pasos prácticos']},
      {badge:'Orientación completa',length:'1.250–1.500 palabras',promise:'Añade cartas aclaratorias y convierte la lectura en un plan práctico de 30 días.',bullets:['Todo lo incluido en Enfocada','Tirada aclaratoria de 3 cartas','Riesgos, protecciones y límites','Plan de 30 días, semana a semana']}
    ],
    recommended:'Empieza con Enfocada',selected:'Seleccionado',selectedLevel:'Nivel de lectura seleccionado',compareShow:'Comparar todas las opciones',compareHide:'Ocultar opciones',
    selectedReason:'Has elegido este nivel. Tu pregunta, enfoque y siete cartas permanecen vinculados durante el pago seguro.',
    reasonEssential:'Tu pregunta busca una respuesta directa de tipo sí/no; el nivel Esencial es el más adecuado.',reasonCompare:'Tu pregunta compara dos direcciones; el nivel Enfocado lee ambos caminos en paralelo.',reasonPlan:'Tu pregunta pide cómo actuar o qué hacer después; el nivel Completo añade cartas aclaratorias y un plan paso a paso.',
    arc1:'Para “{question}”, la carta {pastName} en Pasado aporta {pastMeaning}, mientras la carta {presentName} en Presente aporta {presentMeaning}; juntas muestran cómo el patrón anterior llega a la situación actual.',
    arc2:'Bajo la superficie, la carta {hiddenName} señala {hiddenMeaning}; la carta {obstacleName} convierte {obstacleMeaning} en el obstáculo, mientras la carta {externalName} añade desde fuera {externalMeaning}.',
    arc3:'La carta {adviceName} aconseja {adviceMeaning}; si aplicas esa orientación, la carta {outcomeName} describe una dirección condicional de {outcomeMeaning}, no un veredicto fijo.',
    reversed:'Invertida',upright:'Derecha',card:'Carta',faceDown:'boca abajo. Ábrela.',hintOne:'Una sola situación concreta funciona mejor.',hintMany:'Centra la pregunta en una situación, no en varios temas distintos.',hintFocused:'✓ Pregunta concreta',tableReady:'Tu pregunta está lista. Saca las cartas cuando quieras.',tableWrite:'Escribe tu pregunta para preparar la lectura.',activeEmpty:'Tu tirada está lista para abrirse',activeEmptyLine:'Abre una carta cada vez antes de conectar toda la historia.',activeFrame:'Esta carta es simbólica, no una predicción fija. En esta posición señala un patrón que puedes observar, cuestionar o cambiar.',progress:'{n} de 7 cartas reveladas',cardRevealed:'Carta {n} de 7 revelada.',allRevealed:'Las siete cartas están reveladas.',step3:'Paso 3 de 3 · Tu lectura',viewOptions:'Ver opciones',step1Ask:'Paso 1 de 3 · Pregunta',step1Ready:'Paso 1 de 3 · Pregunta lista',step2Preparing:'Paso 2 de 3 · Preparando',arranging:'Ordenando tu tirada de siete cartas',preparing:'Preparando…',step2Card:'Paso 2 de 3 · Carta {n} de 7',allOpen:'Las siete cartas están abiertas',sampleStep:'Lectura de ejemplo · Paso 3',sampleFlow:'Mira el proceso y saca tus cartas',startMine:'Empezar mi lectura',teaserCta:'Descubrir la condición decisiva',visibleLabel:'Lo visible',visibleLead:'La historia empieza con Pasado y Presente.',finalExampleHeading:'¿Lista para sacar tus siete cartas?',finalExampleBody:'Haz una pregunta concreta y recibe tu tirada de Herradura.',finalHeading:'Tus siete cartas ya están aquí.',finalBody:'Vuelve a tu lectura y elige el nivel de orientación que necesitas.',finalCta:'Volver a mi lectura',checkoutFallback:'El servicio de vista previa no está disponible ahora, pero tus cartas, la síntesis de esta página y el pago seguro siguen disponibles.',quotaNote:'Las tiradas y la síntesis de esta página siguen disponibles. La vista previa mejorada está limitada a 3 solicitudes por cada 24 horas en las herramientas de Deckaura.',checkoutReady:'El pago seguro está listo. Tu pregunta, enfoque y siete cartas seguirán vinculados.',checkoutRetry:'No se abrió el pago. Tu lectura está guardada; inténtalo de nuevo.',priceRetrying:'Reintentando la comprobación segura del precio…',priceConfirming:'Confirmando el precio seguro para tu mercado…',checkoutOpening:'Abriendo el pago seguro…',readingIncomplete:'Completa tu lectura gratuita antes de elegir un paquete.',packageFailed:'No se pudo preparar este paquete. Elígelo de nuevo o prueba otro nivel.',
    faq: [['¿Qué recibiré exactamente?','Una lectura escrita por correo electrónico y una página privada que podrás volver a consultar.'],['¿Qué diferencia hay entre la síntesis gratuita y la lectura de pago?','La síntesis gratuita conecta los significados reales de las siete cartas. La lectura de pago aplica ese patrón a tu pregunta, resuelve tensiones y añade condiciones y pasos prácticos.'],['¿Quién revisa la lectura de pago?',"Tu interpretación se prepara con IA y simbolismo tradicional del tarot a partir de tu pregunta, contexto y cartas, con controles automáticos de calidad. Ayuda a reflexionar; no garantiza resultados ni revela pensamientos privados."],['¿Cuánto tarda la entrega?',''],['¿Qué pasa si escribo mal mi correo?','Responde a la confirmación del pedido o usa el enlace de contacto; lo reenviamos sin coste.'],['¿Puedo preguntar por otra persona?','Sí, si preguntas por la dinámica de la situación. La lectura no afirma conocer pensamientos privados de otra persona.'],['¿Se incluyen cartas invertidas?','Sí. Se leen como un matiz de su posición: bloqueo, demora, interiorización o revisión.'],['¿Puedo cambiar la pregunta después del pago?','Puedes añadir contexto o corregirla durante los primeros 30 minutos si la lectura aún no se ha enviado.'],['¿Es una suscripción?','No. Cada lectura es una compra única.'],['¿Cuál es la política de reembolso?','Es reembolsable antes de que se prepare; una lectura personalizada ya escrita y entregada no puede reembolsarse.'],['¿Cómo se guarda mi pregunta?','Solo se vincula a tu lectura, no se envía a analítica ni se publica. Puedes solicitar su eliminación.']]
  };

  COPY.de = {
    deliveryLong:"Mit KI erstellt · meist in 90 Min.",deliveryShort:"Mit KI erstellt · meist in 90 Min.",intents:['Liebe & Verbindung','Eine Entscheidung','Beruf','Persönliche Entwicklung'],needs:['Hilf mir, die Karten als Ganzes zu verstehen.','Zeige mir, was geschehen kann, wenn ich handle, warte oder den Kurs ändere.','Hilf mir, einen Plan für meine nächsten Schritte zu erstellen.'],tierNames:['Persönliche Basisantwort','Fokussierte persönliche Antwort','Ausführliche persönliche Deutung'],priceLoading:'Sicherer Preis wird geprüft…',priceError:'Preis nicht verfügbar',packageUnavailable:'Dieses Paket ist vorübergehend nicht verfügbar',retryPrice:'Sichere Preisprüfung wiederholen',freeHeading:'Was deine sieben Karten gemeinsam zeigen',freeBridge:'Diese Zusammenfassung verbindet alle sieben Positionen mit den tatsächlichen Bedeutungen deiner Karten. Eine persönliche Deutung ist optional und wendet das Muster auf deine konkrete Situation an.',resultLimit:'Das Kontingent für kostenlose Vorschauen im rollierenden 24-Stunden-Fenster ist erreicht. Deine Karten und der sichere Checkout bleiben verfügbar.',resultFallback:'Deine sieben Karten und ihre Zusammenfassung sind bereit.',revealAll:'Alle 7 Karten aufdecken und kostenlose Zusammenfassung zeigen',showOverview:'Kostenlose Zusammenfassung zeigen',openNext:'Nächste Karte öffnen',questionReady:'Deine Frage ist bereit',writeQuestion:'Eine konkrete Frage schreiben',drawCards:'7 Karten ziehen',drawAgain:'Neue Legung ziehen',startReading:'Kostenlose Deutung starten',openingCheckout:'Sicherer Checkout wird geöffnet…',preparingReading:'Sichere Deutung wird vorbereitet…',chooseTier:'Wählen: ',pricingReady:'Sicherer Preis für deinen Markt bestätigt',pricingLoading:'Sicherer Preis für deinen Markt wird bestätigt…',
    positions:['Vergangenheit','Gegenwart','Verborgene Einflüsse','Hindernis','Äußere Einflüsse','Rat','Wahrscheinliches Ergebnis'],positionInfo:[['01','Vergangenheit','Wie die Situation entstand und was sie noch mitträgt.'],['02','Gegenwart','Wo die Situation heute tatsächlich steht.'],['03','Verborgene Einflüsse','Was unter der Oberfläche wirkt.'],['04','Hindernis','Die Reibung, die den Fortschritt unterbricht.'],['05','Äußere Einflüsse','Menschen, Timing oder Umstände außerhalb deiner Kontrolle.'],['06','Rat','Die Reaktion, die in deiner Hand liegt.'],['07','Wahrscheinliches Ergebnis','Wohin der aktuelle Weg führen kann, wenn das Muster bleibt.']],samples:['Soll ich Kontakt aufnehmen oder warten?','Was geschieht, wenn ich noch ein Jahr in dieser Rolle bleibe?','Welches Muster wiederholt sich in meinen Beziehungen und was könnte es unterbrechen?'],
    packages:[{badge:'Die Legung verstehen',length:'450–600 Wörter',promise:'Verbinde deine sieben Karten zu einer klaren Schlussfolgerung.',bullets:['Die vollständige Geschichte aller 7 Karten','Dein stärkstes Signal und die zentrale Spannung','Die Bedingung, die das Ergebnis prägt','Ein realistischer nächster Schritt']},{badge:'Zwei Wege vergleichen',length:'750–950 Wörter',promise:'Vergleiche den aktuellen Weg mit dem, was sich durch Handeln, Warten oder einen Kurswechsel ändern kann.',bullets:['Alles aus der Basisantwort','Aktueller Weg und veränderter Weg','Was das Ergebnis stärkt oder schwächt','Drei praktische nächste Schritte']},{badge:'Vollständige Orientierung',length:'1.250–1.500 Wörter',promise:'Ergänze Klärungskarten und mache daraus einen praktischen 30-Tage-Plan.',bullets:['Alles aus der fokussierten Antwort','Gezielte Klärungslegung mit 3 Karten','Risiken, Schutz und Grenzen','30-Tage-Plan Woche für Woche']}],recommended:'Mit Fokussiert starten',selected:'Ausgewählt',selectedLevel:'Ausgewählte Deutungstiefe',compareShow:'Alle Optionen vergleichen',compareHide:'Optionen ausblenden',selectedReason:'Du hast diese Tiefe gewählt. Frage, Fokus und sieben Karten bleiben im sicheren Checkout verknüpft.',reasonEssential:'Deine Frage zielt auf eine direkte Ja/Nein-Antwort; die Basisantwort passt am besten.',reasonCompare:'Deine Frage vergleicht zwei Richtungen; die fokussierte Antwort liest beide Wege nebeneinander.',reasonPlan:'Deine Frage fragt nach dem Handeln oder dem nächsten Schritt; die ausführliche Deutung ergänzt Klärungskarten und einen konkreten Plan.',arc1:'Für „{question}“ bringt die Karte {pastName} in der Vergangenheit {pastMeaning}, während die Karte {presentName} in der Gegenwart {presentMeaning} zeigt; zusammen erklären sie, wie das frühere Muster heute weiterwirkt.',arc2:'Unter der Oberfläche weist die Karte {hiddenName} auf {hiddenMeaning}; die Karte {obstacleName} macht {obstacleMeaning} zum Hindernis, während die Karte {externalName} von außen {externalMeaning} hinzufügt.',arc3:'Die Karte {adviceName} rät zu {adviceMeaning}; wenn du diesen Rat anwendest, beschreibt die Karte {outcomeName} mit {outcomeMeaning} eine bedingte Richtung, kein festes Urteil.',reversed:'Umgekehrt',upright:'Aufrecht',card:'Karte',faceDown:'verdeckt. Öffne sie.',hintOne:'Eine konkrete Situation funktioniert am besten.',hintMany:'Konzentriere dich auf eine Situation statt auf mehrere Themen.',hintFocused:'✓ Konkrete Frage',tableReady:'Deine Frage ist bereit. Ziehe, wenn du möchtest.',tableWrite:'Schreibe deine Frage, um die Legung vorzubereiten.',activeEmpty:'Deine Legung kann geöffnet werden',activeEmptyLine:'Öffne jeweils eine Karte, bevor die ganze Geschichte verbunden wird.',activeFrame:'Diese Karte ist symbolisch und keine feste Vorhersage. Hier zeigt sie ein Muster, das du beobachten, hinterfragen oder ändern kannst.',progress:'{n} von 7 Karten aufgedeckt',cardRevealed:'Karte {n} von 7 aufgedeckt.',allRevealed:'Alle sieben Karten sind aufgedeckt.',step3:'Schritt 3 von 3 · Deine Deutung',viewOptions:'Optionen ansehen',step1Ask:'Schritt 1 von 3 · Frage',step1Ready:'Schritt 1 von 3 · Frage bereit',step2Preparing:'Schritt 2 von 3 · Vorbereitung',arranging:'Deine Sieben-Karten-Legung wird angeordnet',preparing:'Wird vorbereitet…',step2Card:'Schritt 2 von 3 · Karte {n} von 7',allOpen:'Alle sieben Karten sind offen',sampleStep:'Beispieldeutung · Schritt 3',sampleFlow:'Ablauf ansehen und eigene Karten ziehen',startMine:'Meine Deutung starten',teaserCta:'Entscheidende Bedingung aufdecken',visibleLabel:'Was sichtbar ist',visibleLead:'Die Geschichte beginnt mit Vergangenheit und Gegenwart.',finalExampleHeading:'Bereit für deine sieben Karten?',finalExampleBody:'Stelle eine konkrete Frage und erhalte deine Hufeisen-Legung.',finalHeading:'Deine sieben Karten sind bereits hier.',finalBody:'Kehre zu deiner Deutung zurück und wähle die gewünschte Tiefe.',finalCta:'Zu meiner Deutung zurück',checkoutFallback:'Der Vorschau-Dienst ist gerade nicht erreichbar. Deine Karten, die Zusammenfassung und der sichere Checkout bleiben verfügbar.',quotaNote:'Kostenlose Legungen und die Zusammenfassung auf dieser Seite bleiben verfügbar. Die erweiterte Vorschau ist auf 3 Anfragen innerhalb von 24 Stunden über Deckaura-Tools begrenzt.',checkoutReady:'Der sichere Checkout ist bereit. Frage, Fokus und sieben Karten bleiben verknüpft.',checkoutRetry:'Der Checkout wurde nicht geöffnet. Deine Deutung ist gespeichert; versuche es erneut.',priceRetrying:'Sichere Preisprüfung wird wiederholt…',priceConfirming:'Sicherer Preis für deinen Markt wird bestätigt…',checkoutOpening:'Sicherer Checkout wird geöffnet…',readingIncomplete:'Schließe zuerst deine kostenlose Deutung ab.',packageFailed:'Dieses Paket konnte nicht vorbereitet werden. Wähle es erneut oder probiere eine andere Tiefe.',faq:[['Was erhalte ich genau?','Eine schriftliche Deutung per E-Mail und eine private Seite, die du erneut aufrufen kannst.'],['Was unterscheidet die kostenlose Zusammenfassung von der bezahlten Deutung?','Die kostenlose Zusammenfassung verbindet die tatsächlichen Bedeutungen deiner sieben Karten. Die bezahlte Deutung wendet sie auf deine Frage an und ergänzt Bedingungen und nächste Schritte.'],['Wer prüft die bezahlte Deutung?',"Deine Deutung wird mit KI und traditioneller Tarot-Symbolik aus deiner Frage, deinem Kontext und deinen Karten erstellt und automatisch auf Qualität geprüft. Sie dient der Reflexion und garantiert keine Ergebnisse oder Einblicke in private Gedanken."],['Wie lange dauert die Lieferung?',''],['Was passiert bei einer falschen E-Mail-Adresse?','Antworte auf die Bestellbestätigung oder nutze den Kontaktlink; wir senden sie kostenlos erneut.'],['Kann ich nach einer anderen Person fragen?','Ja, wenn du nach der Dynamik einer Situation fragst. Die Deutung behauptet keinen Zugang zu privaten Gedanken.'],['Sind umgekehrte Karten enthalten?','Ja. Sie markieren meist Blockade, Verzögerung, Verinnerlichung oder Neubewertung.'],['Kann ich meine Frage nach dem Checkout ändern?','Du kannst in den ersten 30 Minuten Kontext ergänzen oder die Formulierung korrigieren, solange die Deutung nicht versendet wurde.'],['Ist das ein Abonnement?','Nein. Jede Deutung ist ein einmaliger Kauf.'],['Wie lautet die Erstattungsregel?','Vor der Vorbereitung ist eine Erstattung möglich; eine geschriebene und gelieferte Einzelanfertigung kann nicht erstattet werden.'],['Wie wird meine Frage gespeichert?','Sie bleibt nur mit deiner Deutung verbunden, wird nicht an Analysen gesendet oder veröffentlicht und kann gelöscht werden.']]
  };

  Object.assign(COPY.tr, {
    needs:['Kartların birlikte ne anlattığını anlamama yardım et.','Harekete geçer, bekler veya yön değiştirirsem ne olabileceğini göster.','Sonraki adımlarım için bir plan oluşturmama yardım et.'],tierNames:['Temel Kişisel Yanıt','Odaklı Kişisel Yanıt','Kapsamlı Kişisel Yorum'],positions:['Geçmiş','Şimdi','Gizli Etkiler','Engel','Dış Etkiler','Tavsiye','Olası Sonuç'],positionInfo:[['01','Geçmiş','Durumun nasıl oluştuğu ve hâlâ ne taşıdığı.'],['02','Şimdi','Durumun bugün gerçekten nerede olduğu.'],['03','Gizli Etkiler','Yüzeyin altında durumu biçimlendiren etken.'],['04','Engel','İlerlemeyi kesintiye uğratan sürtüşme.'],['05','Dış Etkiler','Kontrolünüz dışındaki insanlar, zamanlama veya koşullar.'],['06','Tavsiye','Kontrolünüzde kalan yaklaşım.'],['07','Olası Sonuç','Örüntü sürerse mevcut yönün varabileceği yer.']],samples:['İletişime geçmeli miyim, yoksa beklemeli miyim?','Bu rolde bir yıl daha kalırsam ne olur?','İlişkilerimde hangi örüntü tekrarlanıyor ve bunu ne kesebilir?'],packages:[{badge:'Açılımı anla',length:'450–600 kelime',promise:'Yedi kartınızı tek ve açık bir sonuca bağlayın.',bullets:['7 kartın tamamındaki hikâye','En güçlü işaretiniz ve temel gerilim','Sonucu biçimlendiren koşul','Gerçekçi bir sonraki adım']},{badge:'İki yönü karşılaştır',length:'750–950 kelime',promise:'Mevcut yolu; harekete geçer, bekler veya yön değiştirirseniz oluşabilecek yolla karşılaştırın.',bullets:['Temel yanıttaki her şey','Mevcut yol ve değişen yol','Sonucu güçlendiren veya zayıflatan etkenler','Üç pratik sonraki adım']},{badge:'Tam rehberlik',length:'1.250–1.500 kelime',promise:'Açıklayıcı kartlar ekleyin ve yorumu 30 günlük pratik plana dönüştürün.',bullets:['Odaklı yanıttaki her şey','Hedefli 3 kartlık açıklama açılımı','Riskler, koruyucu adımlar ve sınırlar','Hafta hafta 30 günlük eylem planı']}],recommended:'Odaklı ile başlayın',selected:'Seçili',selectedLevel:'Seçtiğiniz yorum düzeyi',compareShow:'Tüm seçenekleri karşılaştır',compareHide:'Seçenekleri gizle',selectedReason:'Bu düzeyi siz seçtiniz. Sorunuz, odağınız ve yedi kartınız güvenli ödemede bağlı kalır.',reasonEssential:'Sorunuz doğrudan evet/hayır türünde bir yanıt istiyor; Temel düzey en yakın eşleşme.',reasonCompare:'Sorunuz iki olası yönü karşılaştırıyor; Odaklı düzey her iki yolu yan yana okur.',reasonPlan:'Sorunuz nasıl hareket edeceğinizi veya ne yapacağınızı soruyor; Kapsamlı düzey açıklayıcı kartlar ve adım adım plan ekler.',arc1:'“{question}” sorunuz için Geçmişteki {pastName}, {pastMeaning} temasını; Şimdideki {presentName} ise {presentMeaning} temasını getiriyor; birlikte önceki örüntünün bugünkü duruma nasıl taşındığını gösteriyorlar.',arc2:'Yüzeyin altında {hiddenName}, {hiddenMeaning} temasına işaret ediyor; {obstacleName}, {obstacleMeaning} temasını engele dönüştürürken {externalName} dışarıdan {externalMeaning} etkisini ekliyor.',arc3:'{adviceName}, {adviceMeaning} yönünde davranmayı öneriyor; bu tavsiye uygulanırsa {outcomeName}, sabit bir hüküm değil, {outcomeMeaning} yönünde koşullu bir olasılık anlatıyor.',reversed:'Ters',upright:'Düz',card:'Kart',faceDown:'kapalı. Açın.',hintOne:'Tek ve odaklı bir durum en iyi sonucu verir.',hintMany:'Birbiriyle ilgisiz birkaç soru yerine tek bir duruma odaklanın.',hintFocused:'✓ Odaklı soru',tableReady:'Sorunuz hazır. Hazır olduğunuzda kartları çekin.',tableWrite:'Açılımı hazırlamak için sorunuzu yazın.',activeEmpty:'Açılımınız açılmaya hazır',activeEmptyLine:'Tüm hikâye birleşmeden önce her konuma alan açmak için kartları tek tek açın.',activeFrame:'Bu kart semboliktir; sabit bir kehanet değildir. Bu konumda fark edebileceğiniz, sorgulayabileceğiniz veya değiştirebileceğiniz bir örüntüyü gösterir.',progress:'7 kartın {n} tanesi açıldı',cardRevealed:'7 kartın {n}. kartı açıldı.',allRevealed:'Yedi kartın tümü açıldı.',step3:'3. adım / 3 · Yorumunuz',viewOptions:'Seçenekleri gör',step1Ask:'1. adım / 3 · Sorun',step1Ready:'1. adım / 3 · Soru hazır',step2Preparing:'2. adım / 3 · Hazırlanıyor',arranging:'Yedi kartlık açılımınız düzenleniyor',preparing:'Hazırlanıyor…',step2Card:'2. adım / 3 · 7 kartın {n}. kartı',allOpen:'Yedi kartın tümü açık',sampleStep:'Örnek yorum · 3. adım',sampleFlow:'Akışı görün, sonra kendi kartlarınızı çekin',startMine:'Kendi yorumuma başla',teaserCta:'Belirleyici koşulu aç',visibleLabel:'Görünen örüntü',visibleLead:'Hikâye Geçmiş ve Şimdi ile başlar.',finalExampleHeading:'Kendi yedi kartınızı çekmeye hazır mısınız?',finalExampleBody:'Tek bir odaklı soru sorun ve At Nalı açılımınızı alın.',finalHeading:'Yedi kartınız zaten burada.',finalBody:'Yorumunuza dönün ve ihtiyacınız olan rehberlik düzeyini seçin.',finalCta:'Yorumuma dön',checkoutFallback:'Ücretsiz önizleme hizmeti şu anda kullanılamıyor; kartlarınız, sayfadaki özet ve güvenli ödeme kullanılabilir durumda.',quotaNote:'Ücretsiz kart çekimleri ve bu sayfadaki özet kullanılabilir kalır. Gelişmiş önizleme, Deckaura araçlarında 24 saatlik kayan süre içinde 3 istekle sınırlıdır.',checkoutReady:'Güvenli ödeme hazır. Sorunuz, odağınız ve yedi kartınız bağlı kalacak.',checkoutRetry:'Ödeme açılmadı. Yorumunuz güvende; lütfen yeniden deneyin.',priceRetrying:'Güvenli fiyat kontrolü yeniden deneniyor…',priceConfirming:'Pazarınıza ait güvenli fiyat doğrulanıyor…',checkoutOpening:'Güvenli ödeme açılıyor…',readingIncomplete:'Paket seçmeden önce ücretsiz yorumunuzu tamamlayın.',packageFailed:'Bu paket hazırlanamadı. Yeniden seçin veya başka bir düzey deneyin.',faq:[['Tam olarak ne alacağım?','E-postayla yazılı yorum ve daha sonra dönebileceğiniz özel bir sayfa.'],['Ücretsiz özet ile ücretli yorumun farkı nedir?','Ücretsiz özet yedi kartın gerçek anlamlarını bağlar. Ücretli yorum bu örüntüyü sorunuza uygular, çelişkileri çözer ve koşullar ile pratik adımları ekler.'],['Ücretli yorumu kim inceliyor?',"Yorumunuz yapay zekâ ve geleneksel tarot sembolizmiyle, sorunuz, bağlamınız ve kartlarınız temel alınarak otomatik kalite kontrolleriyle hazırlanır. Yorum düşünmeye yardımcı olur; sonuçları garanti etmez veya özel düşünceleri ortaya çıkarmaz."],['Teslimat ne kadar sürer?',''],['E-posta adresini yanlış yazarsam ne olur?','Sipariş onayını yanıtlayın veya iletişim bağlantısını kullanın; doğru adrese ücretsiz yeniden göndeririz.'],['Başka biri hakkında soru sorabilir miyim?','Durumun dinamiğini sorabilirsiniz. Yorum, başka birinin özel düşüncelerine eriştiğini iddia etmez.'],['Ters kartlar dahil mi?','Evet. Ters kartlar konumun; gecikme, içselleşme, engel veya yeniden değerlendirme yönünü gösterir.'],['Ödemeden sonra sorumu değiştirebilir miyim?','Yorum gönderilmediyse ilk 30 dakika içinde bağlam ekleyebilir veya ifadeyi düzeltebilirsiniz.'],['Bu bir abonelik mi?','Hayır. Her yorum tek seferlik satın alımdır.'],['İade politikası nedir?','Yorum hazırlanmadan önce iade edilebilir; kişiye özel yazılıp teslim edilen ürün iade edilemez.'],['Sorum nasıl saklanıyor?','Yalnızca yorumunuza bağlanır, analitiğe gönderilmez ve yayımlanmaz. Silinmesini isteyebilirsiniz.']]
  });

  Object.assign(COPY.ptbr, {
    positions:['Passado','Presente','Influências ocultas','Obstáculo','Influências externas','Conselho','Resultado provável'],positionInfo:[['01','Passado','Como a situação se formou e o que ainda carrega.'],['02','Presente','Onde a situação realmente está hoje.'],['03','Influências ocultas','O que atua abaixo da superfície.'],['04','Obstáculo','A tensão que interrompe o avanço.'],['05','Influências externas','Pessoas, timing ou condições fora do seu controle.'],['06','Conselho','A resposta que permanece sob seu controle.'],['07','Resultado provável','Aonde a direção atual pode levar se o padrão continuar.']],samples:['Devo entrar em contato ou esperar?','O que acontece se eu ficar mais um ano nesta função?','Que padrão se repete nos meus relacionamentos e o que pode interrompê-lo?'],packages:[{badge:'Entenda a tiragem',length:'450–600 palavras',promise:'Transforme suas sete cartas em uma conclusão clara.',bullets:['A história completa das 7 cartas','Seu sinal mais forte e a tensão central','A condição que molda o resultado','Um próximo passo realista']},{badge:'Compare duas direções',length:'750–950 palavras',promise:'Compare o caminho atual com o que pode mudar se você agir, esperar ou mudar de direção.',bullets:['Tudo da resposta Essencial','Caminho atual e caminho alterado','O que fortalece ou enfraquece o resultado','Três próximos passos práticos']},{badge:'Orientação completa',length:'1.250–1.500 palavras',promise:'Acrescente cartas de esclarecimento e transforme a leitura em um plano prático de 30 dias.',bullets:['Tudo da resposta Focada','Tiragem direcionada de 3 cartas','Riscos, proteções e limites','Plano de 30 dias, semana a semana']}],recommended:'Comece com a Focada',selected:'Selecionado',selectedLevel:'Nível de leitura selecionado',compareShow:'Comparar todas as opções',compareHide:'Ocultar opções',selectedReason:'Você escolheu este nível. Sua pergunta, foco e sete cartas permanecem vinculados no checkout seguro.',reasonEssential:'Sua pergunta busca uma resposta direta de sim/não; o nível Essencial é o mais adequado.',reasonCompare:'Sua pergunta compara duas direções; o nível Focado lê os dois caminhos lado a lado.',reasonPlan:'Sua pergunta pede como agir ou o que fazer em seguida; o nível Completo acrescenta cartas de esclarecimento e um plano passo a passo.',arc1:'Para “{question}”, a carta {pastName} no Passado traz {pastMeaning}, enquanto a carta {presentName} no Presente traz {presentMeaning}; juntas mostram como o padrão anterior chega à situação atual.',arc2:'Abaixo da superfície, a carta {hiddenName} aponta para {hiddenMeaning}; a carta {obstacleName} transforma {obstacleMeaning} no obstáculo, enquanto a carta {externalName} acrescenta de fora {externalMeaning}.',arc3:'A carta {adviceName} aconselha {adviceMeaning}; se essa orientação for aplicada, a carta {outcomeName} descreve uma direção condicional de {outcomeMeaning}, não um veredito fixo.',reversed:'Invertida',upright:'Direita',card:'Carta',faceDown:'virada para baixo. Abra-a.',hintOne:'Uma única situação objetiva funciona melhor.',hintMany:'Concentre a pergunta em uma situação, não em vários assuntos.',hintFocused:'✓ Pergunta objetiva',tableReady:'Sua pergunta está pronta. Tire as cartas quando quiser.',tableWrite:'Escreva sua pergunta para preparar a leitura.',activeEmpty:'Sua tiragem está pronta para ser aberta',activeEmptyLine:'Abra uma carta por vez antes de conectar a história completa.',activeFrame:'Esta carta é simbólica, não uma previsão fixa. Nesta posição, indica um padrão que você pode observar, questionar ou mudar.',progress:'{n} de 7 cartas reveladas',cardRevealed:'Carta {n} de 7 revelada.',allRevealed:'As sete cartas foram reveladas.',step3:'Etapa 3 de 3 · Sua leitura',viewOptions:'Ver opções',step1Ask:'Etapa 1 de 3 · Pergunta',step1Ready:'Etapa 1 de 3 · Pergunta pronta',step2Preparing:'Etapa 2 de 3 · Preparando',arranging:'Organizando sua tiragem de sete cartas',preparing:'Preparando…',step2Card:'Etapa 2 de 3 · Carta {n} de 7',allOpen:'As sete cartas estão abertas',sampleStep:'Leitura de exemplo · Etapa 3',sampleFlow:'Veja o fluxo e tire suas cartas',startMine:'Começar minha leitura',teaserCta:'Revelar a condição decisiva',visibleLabel:'O que está visível',visibleLead:'A história começa com Passado e Presente.',finalExampleHeading:'Pronto para tirar suas sete cartas?',finalExampleBody:'Faça uma pergunta objetiva e receba sua tiragem Ferradura.',finalHeading:'Suas sete cartas já estão aqui.',finalBody:'Volte à leitura e escolha o nível de orientação de que precisa.',finalCta:'Voltar à minha leitura',checkoutFallback:'O serviço de prévia não está disponível agora, mas suas cartas, o resumo da página e o checkout seguro continuam disponíveis.',quotaNote:'As tiragens gratuitas e o resumo desta página continuam disponíveis. A prévia aprimorada é limitada a 3 solicitações em 24 horas entre as ferramentas Deckaura.',checkoutReady:'O checkout seguro está pronto. Sua pergunta, foco e sete cartas permanecerão vinculados.',checkoutRetry:'O checkout não abriu. Sua leitura está segura; tente novamente.',priceRetrying:'Tentando novamente a verificação segura de preço…',priceConfirming:'Confirmando o preço seguro para seu mercado…',checkoutOpening:'Abrindo checkout seguro…',readingIncomplete:'Conclua sua leitura gratuita antes de escolher um pacote.',packageFailed:'Não foi possível preparar este pacote. Selecione-o novamente ou tente outro nível.',faq:[['O que vou receber exatamente?','Uma leitura escrita por e-mail e uma página privada que você pode consultar novamente.'],['Qual é a diferença entre o resumo gratuito e a leitura paga?','O resumo gratuito conecta os significados reais das sete cartas. A leitura paga aplica o padrão à sua pergunta e acrescenta condições e próximos passos.'],['Quem revisa a leitura paga?',"A sua interpretação é preparada com IA e simbolismo tradicional do tarot a partir da pergunta, contexto e cartas, com verificações automáticas de qualidade. Apoia a reflexão; não garante resultados nem revela pensamentos privados."],['Quanto tempo leva a entrega?',''],['E se eu digitar o e-mail errado?','Responda à confirmação do pedido ou use o link de contato; reenviamos sem custo.'],['Posso perguntar sobre outra pessoa?','Sim, quando a pergunta trata da dinâmica da situação. A leitura não afirma acessar pensamentos privados.'],['Cartas invertidas estão incluídas?','Sim. Elas indicam bloqueio, atraso, interiorização ou reavaliação dentro da posição.'],['Posso mudar a pergunta depois do checkout?','Você pode adicionar contexto ou corrigir a frase nos primeiros 30 minutos, se a leitura ainda não tiver sido enviada.'],['É uma assinatura?','Não. Cada leitura é uma compra única.'],['Qual é a política de reembolso?','Há reembolso antes da preparação; uma leitura personalizada já escrita e entregue não pode ser reembolsada.'],['Como minha pergunta é armazenada?','Ela fica vinculada apenas à sua leitura, não é enviada à análise nem publicada e pode ser excluída.']]
  });
  COPY.en.arc3 = 'In Advice, {adviceName} highlights {adviceMeaning} as the pattern to address; if that guidance is applied, {outcomeName} describes a conditional direction of {outcomeMeaning}, not a fixed verdict.';
  COPY.es.arc1 = 'Para “{question}”, la carta {pastName} en Pasado expresa “{pastMeaning}”, mientras la carta {presentName} en Presente expresa “{presentMeaning}”; juntas muestran cómo el patrón anterior llega a la situación actual.';
  COPY.es.arc2 = 'Bajo la superficie, la carta {hiddenName} señala el tema “{hiddenMeaning}”; la carta {obstacleName} presenta “{obstacleMeaning}” como obstáculo, mientras la carta {externalName} añade desde fuera el tema “{externalMeaning}”.';
  COPY.es.arc3 = 'En Consejo, la carta {adviceName} destaca “{adviceMeaning}” como el patrón que conviene atender; si aplicas esa orientación, la carta {outcomeName} describe una dirección condicional marcada por “{outcomeMeaning}”, no un veredicto fijo.';
  COPY.de.arc1 = 'Für „{question}“ steht die Karte {pastName} in der Vergangenheit für „{pastMeaning}“, während die Karte {presentName} in der Gegenwart „{presentMeaning}“ zeigt; zusammen erklären sie, wie das frühere Muster heute weiterwirkt.';
  COPY.de.arc2 = 'Unter der Oberfläche weist die Karte {hiddenName} auf „{hiddenMeaning}“ hin; die Karte {obstacleName} zeigt „{obstacleMeaning}“ als Hindernis, während die Karte {externalName} von außen das Thema „{externalMeaning}“ einbringt.';
  COPY.de.arc3 = 'In der Position Rat macht die Karte {adviceName} auf „{adviceMeaning}“ als zu klärendes Muster aufmerksam; wenn du diesen Rat anwendest, beschreibt die Karte {outcomeName} eine von „{outcomeMeaning}“ geprägte, bedingte Richtung – kein festes Urteil.';
  COPY.tr.arc3 = 'Tavsiye konumundaki {adviceName}, ele alınması gereken örüntü olarak {adviceMeaning} temasını gösteriyor; bu rehberlik uygulanırsa {outcomeName}, sabit bir hüküm değil, {outcomeMeaning} yönünde koşullu bir olasılık anlatıyor.';
  COPY.ptbr.arc1 = 'Para “{question}”, a carta {pastName} no Passado expressa “{pastMeaning}”, enquanto a carta {presentName} no Presente expressa “{presentMeaning}”; juntas mostram como o padrão anterior chega à situação atual.';
  COPY.ptbr.arc2 = 'Abaixo da superfície, a carta {hiddenName} aponta para o tema “{hiddenMeaning}”; a carta {obstacleName} apresenta “{obstacleMeaning}” como obstáculo, enquanto a carta {externalName} acrescenta de fora o tema “{externalMeaning}”.';
  COPY.ptbr.arc3 = 'Em Conselho, a carta {adviceName} destaca “{adviceMeaning}” como o padrão a ser trabalhado; se essa orientação for aplicada, a carta {outcomeName} descreve uma direção condicional marcada por “{outcomeMeaning}”, não um veredito fixo.';

  var TWO_TIER_COPY = {
    en: {
      tierNames: ['Focused Personalized Reading', 'In-Depth Personalized Reading'],
      packages: [
        { badge: 'Recommended', length: '750–950 words', promise: 'Answer the question this spread leaves open with focused guidance.', bullets: ['Your exact question read across all 7 cards', 'The deciding condition and what to verify', 'What could change the current direction', 'Three grounded next steps'] },
        { badge: 'Most complete', length: '1,250–1,500 words', promise: 'Examine the alternatives, risks and timing in a fuller decision reading.', bullets: ['Everything in the Focused reading', 'Alternative paths and likely trade-offs', 'Risks, safeguards and boundaries', 'A practical 30-day action plan'] }
      ],
      surfacePattern: '{pastName} in Past and {presentName} in Present point to a shift from “{pastMeaning}” toward “{presentMeaning}”.',
      surfaceOpenLoop: 'Will the hidden pressure represented by {hiddenName} change before the direction shown by {outcomeName} takes hold?',
      paidDifferenceAnswer: 'The free result shows the surface pattern and the critical question still open. A paid reading applies all seven cards to your exact question, identifies the deciding condition and gives concrete next steps.',
      freeBridge: 'This is the surface pattern across your cards. Choose a reading only if you want the deciding condition and practical next steps applied to your exact question.',
      selectedReason: 'You selected this level. Your question and seven cards stay attached through secure checkout.',
      checkoutReady: 'Secure checkout is ready. Your question and seven cards will stay attached.',
      viewOptions: 'View two options'
    },
    es: {
      tierNames: ['Lectura Personalizada Enfocada', 'Lectura Personalizada Completa'],
      packages: [
        {badge:'Recomendada',length:'750–950 palabras',promise:'Responde la pregunta que esta tirada deja abierta con una orientación enfocada.',bullets:['Tu pregunta exacta leída en las 7 cartas','La condición decisiva y qué verificar','Qué podría cambiar la dirección actual','Tres próximos pasos concretos']},
        {badge:'Más completa',length:'1.250–1.500 palabras',promise:'Examina alternativas, riesgos y tiempos en una lectura de decisión más completa.',bullets:['Todo lo incluido en la lectura Enfocada','Caminos alternativos y sus concesiones','Riesgos, protecciones y límites','Plan práctico de acción de 30 días']}
      ],
      surfacePattern:'La carta {pastName} en Pasado y {presentName} en Presente señalan un cambio desde «{pastMeaning}» hacia «{presentMeaning}».',
      surfaceOpenLoop:'¿Cambiará la presión oculta representada por {hiddenName} antes de que tome fuerza la dirección mostrada por {outcomeName}?',
      paidDifferenceAnswer:'El resultado gratuito muestra el patrón superficial y la pregunta crítica que sigue abierta. La lectura de pago aplica las siete cartas a tu pregunta exacta, identifica la condición decisiva y ofrece próximos pasos concretos.',
      freeBridge:'Este es el patrón superficial de tus cartas. Elige una lectura solo si quieres aplicar la condición decisiva y los próximos pasos a tu pregunta exacta.',
      selectedReason:'Has elegido este nivel. Tu pregunta y tus siete cartas permanecen vinculadas durante el pago seguro.',
      checkoutReady:'El pago seguro está listo. Tu pregunta y tus siete cartas seguirán vinculadas.',
      viewOptions:'Ver dos opciones'
    },
    de: {
      tierNames:['Fokussierte persönliche Deutung','Ausführliche persönliche Deutung'],
      packages:[
        {badge:'Empfohlen',length:'750–950 Wörter',promise:'Beantworte die offene Frage dieser Legung mit fokussierter Orientierung.',bullets:['Deine genaue Frage in allen 7 Karten','Entscheidende Bedingung und Prüfzeichen','Was die aktuelle Richtung verändern kann','Drei konkrete nächste Schritte']},
        {badge:'Am ausführlichsten',length:'1.250–1.500 Wörter',promise:'Prüfe Alternativen, Risiken und Timing in einer ausführlichen Entscheidungsdeutung.',bullets:['Alles aus der fokussierten Deutung','Alternative Wege und Abwägungen','Risiken, Schutz und Grenzen','Praktischer 30-Tage-Aktionsplan']}
      ],
      surfacePattern:'{pastName} in der Vergangenheit und {presentName} in der Gegenwart weisen auf einen Wandel von „{pastMeaning}“ hin zu „{presentMeaning}“ hin.',
      surfaceOpenLoop:'Verändert sich der verborgene Druck, für den {hiddenName} steht, bevor sich die von {outcomeName} gezeigte Richtung durchsetzt?',
      paidDifferenceAnswer:'Das kostenlose Ergebnis zeigt das Oberflächenmuster und die entscheidende offene Frage. Eine bezahlte Deutung wendet alle sieben Karten auf deine genaue Frage an, benennt die entscheidende Bedingung und gibt konkrete nächste Schritte.',
      freeBridge:'Dies ist das Oberflächenmuster deiner Karten. Wähle eine Deutung nur, wenn du die entscheidende Bedingung und praktische Schritte auf deine genaue Frage anwenden möchtest.',
      selectedReason:'Du hast diese Tiefe gewählt. Frage und sieben Karten bleiben im sicheren Checkout verknüpft.',
      checkoutReady:'Der sichere Checkout ist bereit. Frage und sieben Karten bleiben verknüpft.',
      viewOptions:'Zwei Optionen ansehen'
    },
    tr: {
      tierNames:['Odaklı Kişisel Yorum','Kapsamlı Kişisel Yorum'],
      packages:[
        {badge:'Önerilen',length:'750–950 kelime',promise:'Bu açılımın açık bıraktığı soruyu odaklı rehberlikle yanıtlayın.',bullets:['Kesin sorunuzun 7 kart üzerinden yorumu','Belirleyici koşul ve doğrulanacak işaret','Mevcut yönü neyin değiştirebileceği','Üç somut sonraki adım']},
        {badge:'En kapsamlı',length:'1.250–1.500 kelime',promise:'Alternatifleri, riskleri ve zamanlamayı daha kapsamlı bir karar yorumunda inceleyin.',bullets:['Odaklı yorumdaki her şey','Alternatif yollar ve olası bedeller','Riskler, koruyucu adımlar ve sınırlar','Pratik 30 günlük eylem planı']}
      ],
      surfacePattern:'Geçmişteki {pastName} ile Şimdideki {presentName}, “{pastMeaning}” temasından “{presentMeaning}” yönüne bir değişime işaret ediyor.',
      surfaceOpenLoop:'{hiddenName} ile görünen gizli baskı, {outcomeName} kartının gösterdiği yön güçlenmeden önce değişecek mi?',
      paidDifferenceAnswer:'Ücretsiz sonuç yüzeydeki örüntüyü ve açık kalan kritik soruyu gösterir. Ücretli yorum yedi kartın tümünü kesin sorunuza uygular, belirleyici koşulu çıkarır ve somut sonraki adımlar verir.',
      freeBridge:'Bu, kartlarınızdaki yüzeysel örüntüdür. Belirleyici koşulu ve pratik sonraki adımları kesin sorunuza uygulamak istiyorsanız bir yorum seçin.',
      selectedReason:'Bu düzeyi seçtiniz. Sorunuz ve yedi kartınız güvenli ödemede bağlı kalır.',
      checkoutReady:'Güvenli ödeme hazır. Sorunuz ve yedi kartınız bağlı kalacak.',
      viewOptions:'İki seçeneği gör'
    },
    ptbr: {
      tierNames:['Leitura Personalizada Focada','Leitura Personalizada Completa'],
      packages:[
        {badge:'Recomendada',length:'750–950 palavras',promise:'Responda à pergunta que esta tiragem deixa em aberto com orientação focada.',bullets:['Sua pergunta exata lida nas 7 cartas','A condição decisiva e o que verificar','O que pode mudar a direção atual','Três próximos passos concretos']},
        {badge:'Mais completa',length:'1.250–1.500 palavras',promise:'Examine alternativas, riscos e timing em uma leitura de decisão mais completa.',bullets:['Tudo da leitura Focada','Caminhos alternativos e concessões','Riscos, proteções e limites','Plano prático de ação de 30 dias']}
      ],
      surfacePattern:'{pastName} no Passado e {presentName} no Presente apontam para uma mudança de “{pastMeaning}” em direção a “{presentMeaning}”.',
      surfaceOpenLoop:'A pressão oculta representada por {hiddenName} mudará antes que a direção mostrada por {outcomeName} ganhe força?',
      paidDifferenceAnswer:'O resultado gratuito mostra o padrão superficial e a pergunta crítica que continua aberta. A leitura paga aplica as sete cartas à sua pergunta exata, identifica a condição decisiva e oferece próximos passos concretos.',
      freeBridge:'Este é o padrão superficial das suas cartas. Escolha uma leitura apenas se quiser aplicar a condição decisiva e os próximos passos à sua pergunta exata.',
      selectedReason:'Você escolheu este nível. Sua pergunta e as sete cartas permanecem vinculadas no checkout seguro.',
      checkoutReady:'O checkout seguro está pronto. Sua pergunta e as sete cartas permanecerão vinculadas.',
      viewOptions:'Ver duas opções'
    },
    pt: {
      tierNames:['Leitura Personalizada Focada','Leitura Personalizada Completa'],
      packages:[
        {badge:'Recomendada',length:'750–950 palavras',promise:'Responda à pergunta que esta tiragem deixa em aberto com orientação focada.',bullets:['A sua pergunta exata lida nas 7 cartas','A condição decisiva e o que confirmar','O que pode mudar a direção atual','Três próximos passos concretos']},
        {badge:'Mais completa',length:'1.250–1.500 palavras',promise:'Examine alternativas, riscos e timing numa leitura de decisão mais completa.',bullets:['Tudo da leitura Focada','Caminhos alternativos e compromissos','Riscos, proteções e limites','Plano prático de ação de 30 dias']}
      ],
      surfacePattern:'{pastName} no Passado e {presentName} no Presente apontam para uma mudança de “{pastMeaning}” em direção a “{presentMeaning}”.',
      surfaceOpenLoop:'A pressão oculta representada por {hiddenName} mudará antes de a direção mostrada por {outcomeName} ganhar força?',
      paidDifferenceAnswer:'O resultado gratuito mostra o padrão superficial e a pergunta crítica que continua aberta. A leitura paga aplica as sete cartas à sua pergunta exata, identifica a condição decisiva e apresenta próximos passos concretos.',
      freeBridge:'Este é o padrão superficial das suas cartas. Escolha uma leitura apenas se quiser aplicar a condição decisiva e os próximos passos à sua pergunta exata.',
      selectedReason:'Escolheu este nível. A sua pergunta e as sete cartas permanecem ligadas no checkout seguro.',
      checkoutReady:'O checkout seguro está pronto. A sua pergunta e as sete cartas permanecerão ligadas.',
      viewOptions:'Ver duas opções'
    }
  };
  Object.keys(TWO_TIER_COPY).forEach(function (lang) {
    if (lang === 'pt' && !COPY.pt) COPY.pt = Object.assign({}, COPY.ptbr);
    Object.assign(COPY[lang] || (COPY[lang] = {}), TWO_TIER_COPY[lang]);
  });

  function copy(key) {
    var lang = localeContext().lang;
    var dict = COPY[lang] || (lang === 'pt' ? COPY.ptbr : COPY.en);
    return dict[key] != null ? dict[key] : COPY.en[key];
  }

  function trKey(value) {
    var dict = window.DeckauraToolTranslations || {};
    return String(dict[value] || value || '');
  }

  var SC7_MAJOR_NAMES = [
    'The Fool','The Magician','The High Priestess','The Empress','The Emperor','The Hierophant','The Lovers','The Chariot','Strength','The Hermit','Wheel of Fortune','Justice','The Hanged Man','Death','Temperance','The Devil','The Tower','The Star','The Moon','The Sun','Judgement','The World'
  ];
  var SC7_NATIVE_NAMES = {
    es: {
      majors:['El Loco','El Mago','La Sacerdotisa','La Emperatriz','El Emperador','El Hierofante','Los Enamorados','El Carro','La Fuerza','El Ermitaño','La Rueda de la Fortuna','La Justicia','El Colgado','La Muerte','La Templanza','El Diablo','La Torre','La Estrella','La Luna','El Sol','El Juicio','El Mundo'],
      ranks:{Ace:'As',Two:'Dos',Three:'Tres',Four:'Cuatro',Five:'Cinco',Six:'Seis',Seven:'Siete',Eight:'Ocho',Nine:'Nueve',Ten:'Diez',Page:'Sota',Knight:'Caballero',Queen:'Reina',King:'Rey'},
      suits:{Cups:'Copas',Pentacles:'Oros',Swords:'Espadas',Wands:'Bastos'}, join:function (rank, suit) { return rank + ' de ' + suit; }
    },
    de: {
      majors:['Der Narr','Der Magier','Die Hohepriesterin','Die Herrscherin','Der Herrscher','Der Hierophant','Die Liebenden','Der Wagen','Die Kraft','Der Eremit','Das Rad des Schicksals','Die Gerechtigkeit','Der Gehängte','Der Tod','Die Mäßigkeit','Der Teufel','Der Turm','Der Stern','Der Mond','Die Sonne','Das Gericht','Die Welt'],
      ranks:{Ace:'Ass',Two:'Zwei',Three:'Drei',Four:'Vier',Five:'Fünf',Six:'Sechs',Seven:'Sieben',Eight:'Acht',Nine:'Neun',Ten:'Zehn',Page:'Bube',Knight:'Ritter',Queen:'Königin',King:'König'},
      suits:{Cups:'Kelche',Pentacles:'Münzen',Swords:'Schwerter',Wands:'Stäbe'}, join:function (rank, suit) { return rank + ' der ' + suit; }
    },
    ptbr: {
      majors:['O Louco','O Mago','A Sacerdotisa','A Imperatriz','O Imperador','O Hierofante','Os Enamorados','O Carro','A Força','O Eremita','A Roda da Fortuna','A Justiça','O Enforcado','A Morte','A Temperança','O Diabo','A Torre','A Estrela','A Lua','O Sol','O Julgamento','O Mundo'],
      ranks:{Ace:'Ás',Two:'Dois',Three:'Três',Four:'Quatro',Five:'Cinco',Six:'Seis',Seven:'Sete',Eight:'Oito',Nine:'Nove',Ten:'Dez',Page:'Pajem',Knight:'Cavaleiro',Queen:'Rainha',King:'Rei'},
      suits:{Cups:'Copas',Pentacles:'Ouros',Swords:'Espadas',Wands:'Paus'}, join:function (rank, suit) { return rank + ' de ' + suit; }
    }
  };
  function nativeCardName(name, lang) {
    var locale = SC7_NATIVE_NAMES[lang === 'pt' ? 'ptbr' : lang];
    if (!locale) return '';
    var majorIndex = SC7_MAJOR_NAMES.indexOf(name);
    if (majorIndex >= 0) return locale.majors[majorIndex];
    var match = /^(Ace|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|Page|Knight|Queen|King) of (Cups|Pentacles|Swords|Wands)$/.exec(name);
    return match ? locale.join(locale.ranks[match[1]], locale.suits[match[2]]) : '';
  }

  function localizedCardName(name) {
    var lang = localeContext().lang;
    if (lang === 'tr' && window.DeckauraTarotTR && window.DeckauraTarotTR[name]) {
      return window.DeckauraTarotTR[name].n || name;
    }
    if (lang === 'en') return name;
    return nativeCardName(name, lang) || trKey(name);
  }

  function sentenceCase(value) {
    value = String(value || '').trim().replace(/[.!?]+$/, '');
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }
  function germanKeywordCase(value) {
    var trigger = /^(?:ein|eine|einen|einem|einer|eines|der|die|das|den|dem|des|von|vom|zu|zum|zur|mit|ohne|im|in|auf|aus|für|vor|nach|bei|durch|gegen|über|unter|zwischen)$/i;
    return String(value || '').split(',').map(function (part) {
      var words = part.trim().split(/\s+/).filter(Boolean);
      return words.map(function (word, index) {
        var shouldCap = index === 0 || index === words.length - 1 || (index > 0 && trigger.test(words[index - 1]));
        return shouldCap ? word.charAt(0).toLocaleUpperCase('de-DE') + word.slice(1) : word;
      }).join(' ');
    }).join(', ');
  }

  function canonicalMeaning(card, reversed) {
    var keys = window.SC7CardMeaningKeys && window.SC7CardMeaningKeys[card.name];
    return keys ? keys[reversed ? 1 : 0] : String(reversed ? (card.reversed || card.upright) : card.upright || '');
  }
  var SC7_MEANING_OVERRIDES = {
    es: {
      'lasting happiness, harmony, emotional fulfillment':'felicidad duradera, armonía, plenitud emocional',
      'strategy, secrecy, quiet maneuvering':'estrategia, discreción, maniobras cuidadosas',
      'abundance, nurturing, creative growth':'abundancia, cuidado, crecimiento creativo',
      'balance, adaptability, juggled priorities':'equilibrio, adaptabilidad, prioridades simultáneas',
      'haste, burnout, reckless speed':'prisa, agotamiento, velocidad temeraria',
      'heartbreak, sorrow, a painful truth':'desamor, pena, una verdad dolorosa',
      'release, a new perspective, reclaimed freedom':'liberación, una nueva perspectiva, libertad recuperada',
      'release, breaking free, reclaimed power':'liberación, independencia, poder recuperado',
      'burnout, perfectionism, waning motivation':'agotamiento, perfeccionismo, motivación menguante',
      'rest, recovery, quiet contemplation':'descanso, recuperación, contemplación serena',
      'weariness, defensiveness, near burnout':'cansancio, actitud defensiva, agotamiento cercano',
      'passion, adventure, bold pursuit':'pasión, aventura, iniciativa audaz',
      'standing your ground, defense, perseverance':'mantenerse firme, defensa, perseverancia',
      'living in the past, stuck sentimentality, rose tinted memory':'apego al pasado, nostalgia estancada, recuerdos idealizados'
    },
    de: {
      'endings, transformation, rebirth':'Abschluss, Wandlung, Neubeginn',
      'manipulation, poor planning, untapped talent':'Manipulation, schwache Planung, ungenutztes Talent',
      'reliability, routine, patient progress':'Verlässlichkeit, Routine, geduldiger Fortschritt',
      'compassion, emotional depth, gentle care':'Mitgefühl, emotionale Tiefe, behutsame Fürsorge',
      'clarity, authority, ethical truth':'Klarheit, Autorität, ethische Wahrheit',
      'burnout, perfectionism, waning motivation':'Erschöpfung, Perfektionismus, nachlassende Motivation',
      'rest, recovery, quiet contemplation':'Ruhe, Erholung, stille Einkehr',
      'burnout, restlessness, stalled recovery':'Erschöpfung, Unruhe, stockende Erholung',
      'haste, burnout, reckless speed':'Eile, Erschöpfung, riskantes Tempo',
      'weariness, defensiveness, near burnout':'Müdigkeit, Abwehrhaltung, drohende Erschöpfung',
      'passion, adventure, bold pursuit':'Leidenschaft, Abenteuer, entschlossenes Streben',
      'standing your ground, defense, perseverance':'Standhaftigkeit, Verteidigung, Ausdauer',
      'coming clean, conscience, a revealed truth':'Ehrlichkeit, Gewissen, eine enthüllte Wahrheit',
      'inspiration, a creative spark, new potential':'Inspiration, kreativer Funke, neues Potenzial',
      'walking away, seeking more, a brave departure':'Loslassen, Sinnsuche, mutiger Aufbruch',
      'choices, dreams, tempting illusions':'Entscheidungen, Träume, verlockende Illusionen',
      'partnership, mutual attraction, deep connection':'Partnerschaft, gegenseitige Anziehung, tiefe Verbindung',
      'coldness, moodiness, guarded emotion':'Kälte, Launenhaftigkeit, verschlossene Gefühle'
    },
    ptbr: {
      'strategy, secrecy, quiet maneuvering':'estratégia, discrição, movimentos cuidadosos',
      'burnout, perfectionism, waning motivation':'esgotamento, perfeccionismo, motivação em queda',
      'rest, recovery, quiet contemplation':'descanso, recuperação, contemplação serena',
      'burnout, restlessness, stalled recovery':'esgotamento, inquietação, recuperação estagnada',
      'haste, burnout, reckless speed':'pressa, esgotamento, velocidade imprudente',
      'weariness, defensiveness, near burnout':'cansaço, atitude defensiva, esgotamento próximo',
      'passion, adventure, bold pursuit':'paixão, aventura, busca corajosa',
      'standing your ground, defense, perseverance':'manter a posição, defesa, perseverança',
      'clearing confusion, releasing fear, truth emerging':'clareza após a confusão, liberação do medo, verdade emergente'
    }
  };

  function focusedMeaning(card, reversed) {
    var lang = localeContext().lang;
    if (lang === 'tr' && window.DeckauraTarotTR && window.DeckauraTarotTR[card.name]) {
      var localized = window.DeckauraTarotTR[card.name];
      return sentenceCase(reversed ? (localized.r || localized.u) : localized.u);
    }
    if (lang === 'en') {
      return sentenceCase(canonicalMeaning(card, reversed));
    }
    var canonical = canonicalMeaning(card, reversed);
    var localeOverrides = SC7_MEANING_OVERRIDES[lang === 'pt' ? 'ptbr' : lang] || {};
    var localizedMeaning = localeOverrides[canonical] || trKey(canonical);
    return lang === 'de' ? String(localeOverrides[canonical] || localizedMeaning || '').trim().replace(/[.!?]+$/, '') : sentenceCase(localizedMeaning);
  }
  var STATIC_COPY = {
    ptbr: {
      freeReading: 'Leitura gratuita', horseshoe: 'Tiragem clássica da Ferradura',
      heroLede: 'Veja o que está moldando esta situação.', questionLabel: 'Sua pergunta — obrigatória',
      questionHelp: 'Concentre-se em uma situação para obter a leitura mais clara.', focusOptional: 'Foco da pergunta — opcional',
      questionError: 'Escreva sua pergunta primeiro — uma frase é suficiente.', preparingTitle: 'Mantenha sua pergunta em mente.',
      preparingBody: 'Suas sete cartas estão sendo organizadas.', showFirst: 'Mostrar a primeira carta agora',
      yourQuestion: 'Sua pergunta', changeQuestion: 'Mudar minha pergunta', freePattern: 'Seu padrão gratuito', connected: 'As sete posições conectadas',
      conditional: 'O resultado continua condicional', deeperEye: 'Aprofunde com uma síntese pessoal',
      deeperHead: 'A leitura gratuita descreve o padrão das cartas. Uma leitura escrita opcional usa a mesma pergunta e cartas para explorar a condição decisiva, diferentes possibilidades de ritmo e próximos passos práticos na profundidade escolhida.',
      deeperBody: 'A leitura gratuita descreve o padrão das cartas. Uma leitura escrita opcional usa a mesma pergunta e cartas para explorar a condição decisiva, diferentes possibilidades de ritmo e próximos passos práticos na profundidade escolhida.',
      offerEye: 'Escolha sua orientação', offerHead: 'Suas sete cartas contam uma história. Escolha o que você precisa dela.',
      offerBody: 'Uma leitura personalizada conecta as sete cartas à sua situação, identifica a condição que molda o resultado e ajuda você a decidir o próximo passo.',
      needMost: 'O que você mais precisa desta leitura?', securePurchase: 'Compra única e segura · Entrega por e-mail',
      cardDetails: 'Detalhes das suas cartas', exploreCards: 'Explorar as sete cartas'
    }
  };
  Object.assign(STATIC_COPY.ptbr, {
    heroTitle:'Leitura de Tarot Gratuita com 7 Cartas',heroSubtitle:'A Tiragem Ferradura',heroSummary:'Sete cartas revelam como a situação se formou, o que a influencia agora e qual escolha pode mudar sua direção provável.',mobileDeck:'7 cartas únicas · tiradas do baralho completo de 78 cartas',privacy:'Privada e vinculada a esta leitura.',storage:'Armazenamento e exclusão',noSignup:'Sem cadastro',privateQuestion:'Pergunta privada',quotaProof:'Resumo gratuito · 3 prévias aprimoradas / 24h',backReading:'Voltar à minha leitura',includePositions:'✓ Todas as 7 posições',includeMeanings:'✓ Significados direitos e invertidos',includeOverview:'✓ Resumo escrito',howWorksLink:'Como funciona a leitura de sete cartas',exampleLink:'Ver uma leitura de exemplo',readingTable:'Sua mesa de leitura',spreadHere:'Sua tiragem de sete cartas aparecerá aqui.',stepAsk:'Escreva uma pergunta objetiva',stepDraw:'Tire a Ferradura',stepOpen:'Abra as cartas uma por vez',freeIncludes:'Grátis com sua tiragem',freeItemCards:'As sete cartas, abertas uma posição por vez',freeItemMeanings:'Significados direitos e invertidos em cada posição',freeItemOverview:'Um resumo escrito do que a tiragem indica',cardDetails:'Conselho e resultado',exploreCards:'Explorar as outras cinco cartas',offerEye:'Orientação pessoal opcional',offerHead:'Escolha apenas a profundidade de que precisa.',offerBody:'Sua pergunta, foco e sete cartas permanecem vinculados. Cada opção identifica a condição decisiva e os próximos passos.',needMost:'O que você mais precisa desta leitura?'
  });
  STATIC_COPY.es = {
    freeReading:'Lectura gratuita',horseshoe:'Tirada clásica de la Herradura',heroTitle:'Lectura de Tarot Gratis de 7 Cartas',heroSubtitle:'La Tirada de la Herradura',heroLede:'Descubre qué está moldeando esta situación.',heroSummary:'Siete cartas revelan cómo se formó la situación, qué influye ahora y qué elección puede cambiar su dirección probable.',mobileDeck:'7 cartas únicas · del mazo completo de 78 cartas',questionLabel:'Tu pregunta — obligatoria',questionHelp:'Céntrate en una situación para obtener una lectura clara.',privacy:'Privada y vinculada a esta lectura.',storage:'Almacenamiento y eliminación',noSignup:'Sin registro',privateQuestion:'Pregunta privada',quotaProof:'Síntesis gratuita · 3 vistas mejoradas / 24 h',questionError:'Escribe primero tu pregunta; una frase es suficiente.',backReading:'Volver a mi lectura',includePositions:'✓ Las 7 posiciones',includeMeanings:'✓ Significados derechos e invertidos',includeOverview:'✓ Síntesis escrita',focusOptional:'Enfoque de la pregunta — opcional',howWorksLink:'Cómo funciona la lectura de siete cartas',exampleLink:'Ver una lectura de ejemplo',readingTable:'Tu mesa de lectura',spreadHere:'Tu tirada de siete cartas aparecerá aquí.',stepAsk:'Escribe una pregunta concreta',stepDraw:'Saca la tirada de Herradura',stepOpen:'Abre las cartas una por una',freeIncludes:'Gratis con tu tirada',freeItemCards:'Las siete cartas, una posición cada vez',freeItemMeanings:'Significados derechos e invertidos en cada posición',freeItemOverview:'Una síntesis escrita de lo que señala la tirada',yourQuestion:'Tu pregunta',changeQuestion:'Cambiar mi pregunta',questionError:'Escribe primero tu pregunta; una frase es suficiente.',preparingTitle:'Mantén tu pregunta en mente.',preparingBody:'Estamos ordenando tus siete cartas.',showFirst:'Mostrar ahora la primera carta',freePattern:'Tu patrón gratuito',connected:'Las siete posiciones conectadas',conditional:'El resultado sigue siendo condicional',offerEye:'Orientación personal opcional',offerHead:'Elige solo la profundidad que necesitas.',offerBody:'Tu pregunta, enfoque y siete cartas permanecen vinculados. Cada opción identifica la condición decisiva y los siguientes pasos.',needMost:'¿Qué necesitas más de esta lectura?',cardDetails:'Consejo y resultado',exploreCards:'Explorar las otras cinco cartas'
  };
  STATIC_COPY.de = {
    freeReading:'Kostenlose Deutung',horseshoe:'Klassische Hufeisen-Legung',heroTitle:'Kostenlose 7-Karten-Tarotdeutung',heroSubtitle:'Die Hufeisen-Legung',heroLede:'Erkenne, was diese Situation prägt.',heroSummary:'Sieben Karten zeigen, wie die Situation entstand, was heute wirkt und welche Entscheidung ihre wahrscheinliche Richtung verändern kann.',mobileDeck:'7 einzigartige Karten · aus dem vollständigen 78-Karten-Deck',questionLabel:'Deine Frage — erforderlich',questionHelp:'Konzentriere dich für eine klare Deutung auf eine Situation.',privacy:'Privat und mit dieser Deutung verknüpft.',storage:'Speicherung und Löschung',noSignup:'Keine Anmeldung',privateQuestion:'Private Frage',quotaProof:'Kostenlose Zusammenfassung · 3 erweiterte Vorschauen / 24 Std.',questionError:'Schreibe zuerst deine Frage; ein Satz genügt.',backReading:'Zu meiner Deutung zurück',includePositions:'✓ Alle 7 Positionen',includeMeanings:'✓ Aufrechte und umgekehrte Bedeutungen',includeOverview:'✓ Schriftliche Zusammenfassung',focusOptional:'Fragefokus — optional',howWorksLink:'So funktioniert die Sieben-Karten-Deutung',exampleLink:'Beispieldeutung ansehen',readingTable:'Dein Legungsplatz',spreadHere:'Deine Sieben-Karten-Legung erscheint hier.',stepAsk:'Eine konkrete Frage schreiben',stepDraw:'Hufeisen-Legung ziehen',stepOpen:'Karten einzeln öffnen',freeIncludes:'Kostenlos in deiner Legung',freeItemCards:'Alle sieben Karten, Position für Position geöffnet',freeItemMeanings:'Aufrechte und umgekehrte Bedeutung jeder Position',freeItemOverview:'Eine schriftliche Zusammenfassung der Legung',yourQuestion:'Deine Frage',changeQuestion:'Frage ändern',preparingTitle:'Behalte deine Frage im Sinn.',preparingBody:'Deine sieben Karten werden angeordnet.',showFirst:'Erste Karte jetzt zeigen',freePattern:'Dein kostenloses Muster',connected:'Alle sieben Positionen verbunden',conditional:'Das Ergebnis bleibt bedingt',offerEye:'Optionale persönliche Orientierung',offerHead:'Wähle nur die Tiefe, die du brauchst.',offerBody:'Frage, Fokus und sieben Karten bleiben verknüpft. Jede Option benennt die entscheidende Bedingung und nächste Schritte.',needMost:'Was brauchst du am meisten aus dieser Deutung?',cardDetails:'Rat und Ergebnis',exploreCards:'Die anderen fünf Karten ansehen'
  };
  Object.assign(STATIC_COPY.tr || (STATIC_COPY.tr = {}), {
    freeReading:'Ücretsiz yorum',horseshoe:'Klasik At Nalı açılımı',heroTitle:'Ücretsiz 7 Kart Tarot Yorumu',heroSubtitle:'At Nalı Açılımı',heroLede:'Bu durumu neyin biçimlendirdiğini görün.',heroSummary:'Yedi kart durumun nasıl geliştiğini, şimdi neyin etkili olduğunu ve hangi seçimin olası yönü değiştirebileceğini gösterir.',mobileDeck:'7 benzersiz kart · 78 kartlık tam desteden',questionLabel:'Sorunuz — zorunlu',questionHelp:'En açık yorum için tek bir duruma odaklanın.',privacy:'Gizlidir ve bu yoruma bağlıdır.',storage:'Saklama ve silme',noSignup:'Kayıt gerekmez',privateQuestion:'Gizli soru',quotaProof:'Ücretsiz özet · 24 saatte 3 gelişmiş önizleme',questionError:'Önce sorunuzu yazın; tek cümle yeterlidir.',backReading:'Yorumuma dön',includePositions:'✓ 7 kart konumunun tümü',includeMeanings:'✓ Düz ve ters anlamlar',includeOverview:'✓ Yazılı özet',focusOptional:'Soru odağı — isteğe bağlı',howWorksLink:'Yedi kart yorumu nasıl çalışır?',exampleLink:'Örnek bir yorum görün',readingTable:'Yorum masanız',spreadHere:'Yedi kartlık açılımınız burada görünecek.',stepAsk:'Tek bir odaklı soru yazın',stepDraw:'At Nalı açılımını çekin',stepOpen:'Kartları tek tek açın',freeIncludes:'Açılımınızla ücretsiz',freeItemCards:'Yedi kartın tümü, konum konum açılır',freeItemMeanings:'Her konum için düz ve ters anlamlar',freeItemOverview:'Açılımın gösterdiği örüntünün yazılı özeti',yourQuestion:'Sorunuz',changeQuestion:'Sorumu değiştir',preparingTitle:'Sorunuzu aklınızda tutun.',preparingBody:'Yedi kartınız düzenleniyor.',showFirst:'İlk kartı şimdi göster',freePattern:'Ücretsiz örüntünüz',connected:'Yedi konumun tümü bağlantılı',conditional:'Sonuç koşullu kalır',offerEye:'İsteğe bağlı kişisel rehberlik',offerHead:'Yalnızca ihtiyacınız olan derinliği seçin.',offerBody:'Sorunuz, odağınız ve yedi kartınız bağlı kalır. Her seçenek belirleyici koşulu ve sonraki adımları açıklar.',needMost:'Bu yorumdan en çok neye ihtiyacınız var?',cardDetails:'Tavsiye ve olası sonuç',exploreCards:'Diğer beş kartı incele'
  });
  Object.assign(STATIC_COPY.es, {
    instantOverview:'Síntesis gratuita instantánea',reviewedSelin:'Lectura escrita privada',exampleReading:'Lectura de ejemplo',sampleNotYours:'Esta es una tirada de muestra, no tu lectura.',startOwn:'Empezar mi propia lectura',secureOneTime:'Compra única y segura',emailDelivery:'Entrega por correo',refundEligibility:'Condiciones de reembolso',upgradeNote:'Elige la profundidad que necesitas. Todas las opciones son compras únicas, sin suscripción.',trustOne:'Compra única',trustPrivate:'Lectura escrita privada',trustSecure:'Pago seguro',trustKeep:'Tuya para conservar',lockedLabel:'Incluido en tu lectura personal',includedIn:'Incluido en'
  });
  Object.assign(STATIC_COPY.de, {
    instantOverview:'Sofortige kostenlose Zusammenfassung',reviewedSelin:'Private schriftliche Deutung',exampleReading:'Beispieldeutung',sampleNotYours:'Dies ist eine Musterlegung, nicht deine Deutung.',startOwn:'Eigene Deutung beginnen',secureOneTime:'Sicherer Einmalkauf',emailDelivery:'Lieferung per E-Mail',refundEligibility:'Erstattungsbedingungen',upgradeNote:'Wähle die Tiefe, die du brauchst. Jede Option ist ein Einmalkauf ohne Abonnement.',trustOne:'Einmalkauf',trustPrivate:'Private schriftliche Deutung',trustSecure:'Sicherer Checkout',trustKeep:'Dauerhaft für dich',lockedLabel:'In deiner persönlichen Deutung enthalten',includedIn:'Enthalten in'
  });
  Object.assign(STATIC_COPY.tr, {
    instantOverview:'Anında ücretsiz özet',reviewedSelin:'Özel yazılı yorum',exampleReading:'Örnek yorum',sampleNotYours:'Bu örnek bir açılımdır; sizin yorumunuz değildir.',startOwn:'Kendi yorumumu başlat',secureOneTime:'Güvenli tek seferlik satın alım',emailDelivery:'E-postayla teslim',refundEligibility:'İade uygunluğu',upgradeNote:'İhtiyacınız olan derinliği seçin. Her seçenek aboneliksiz, tek seferlik satın alımdır.',trustOne:'Tek seferlik satın alım',trustPrivate:'Gizli yazılı yorum',trustSecure:'Güvenli ödeme',trustKeep:'Sizde kalır',lockedLabel:'Kişisel yorumunuza dâhil',includedIn:'Dâhil olduğu paket'
  });
  Object.assign(STATIC_COPY.ptbr, {
    instantOverview:'Resumo gratuito imediato',reviewedSelin:'Leitura escrita privada',exampleReading:'Leitura de exemplo',sampleNotYours:'Esta é uma tiragem de amostra, não a sua leitura.',startOwn:'Começar minha leitura',secureOneTime:'Compra única e segura',emailDelivery:'Entrega por e-mail',refundEligibility:'Condições de reembolso',upgradeNote:'Escolha a profundidade de que precisa. Todas as opções são compras únicas, sem assinatura.',trustOne:'Compra única',trustPrivate:'Leitura escrita privada',trustSecure:'Checkout seguro',trustKeep:'Sua para guardar',lockedLabel:'Incluído na sua leitura pessoal',includedIn:'Incluído em'
  });
  Object.assign(STATIC_COPY.en || (STATIC_COPY.en = {}), { needsAria:'What do you need most from this reading?', levelsAria:'All reading levels', progressAria:'Card reveal progress' });
  Object.assign(STATIC_COPY.es || (STATIC_COPY.es = {}), { needsAria:'¿Qué necesitas más de esta lectura?', levelsAria:'Todos los niveles de lectura', progressAria:'Progreso al revelar las cartas' });
  Object.assign(STATIC_COPY.de || (STATIC_COPY.de = {}), { needsAria:'Was brauchst du am meisten aus dieser Deutung?', levelsAria:'Alle Deutungstiefen', progressAria:'Fortschritt beim Aufdecken der Karten' });
  Object.assign(STATIC_COPY.tr || (STATIC_COPY.tr = {}), { needsAria:'Bu yorumdan en çok neye ihtiyacınız var?', levelsAria:'Tüm yorum düzeyleri', progressAria:'Kart açma ilerlemesi' });
  Object.assign(STATIC_COPY.ptbr || (STATIC_COPY.ptbr = {}), { needsAria:'Do que você mais precisa nesta leitura?', levelsAria:'Todos os níveis de leitura', progressAria:'Progresso da revelação das cartas' });
  Object.assign(STATIC_COPY.en || (STATIC_COPY.en = {}), { reviewedSelin: 'Private written reading' });
  var TWO_TIER_STATIC_COPY = {
    en:{surfaceEye:'Your free surface reading',openLoopEye:'The critical question still open',offerEye:'Choose your reading',offerHead:'Ask the question your free reading leaves open.',offerBody:'Choose one of two depths. Your exact question and seven cards continue securely with the reading.',contextAttached:'Question and seven cards stay connected',exploreCards:'Explore all seven cards in order',levelsAria:'Choose between two reading depths'},
    es:{surfaceEye:'Tu lectura superficial gratuita',openLoopEye:'La pregunta crítica que sigue abierta',offerEye:'Elige tu lectura',offerHead:'Responde la pregunta que tu lectura gratuita deja abierta.',offerBody:'Elige una de dos profundidades. Tu pregunta exacta y tus siete cartas continúan de forma segura con la lectura.',contextAttached:'La pregunta y las siete cartas siguen vinculadas',exploreCards:'Explorar las siete cartas en orden',levelsAria:'Elige entre dos profundidades de lectura'},
    de:{surfaceEye:'Deine kostenlose Oberflächendeutung',openLoopEye:'Die entscheidende offene Frage',offerEye:'Deutung wählen',offerHead:'Beantworte die Frage, die deine kostenlose Deutung offenlässt.',offerBody:'Wähle eine von zwei Tiefen. Deine genaue Frage und sieben Karten bleiben sicher mit der Deutung verknüpft.',contextAttached:'Frage und sieben Karten bleiben verknüpft',exploreCards:'Alle sieben Karten der Reihe nach ansehen',levelsAria:'Zwischen zwei Deutungstiefen wählen'},
    tr:{surfaceEye:'Ücretsiz yüzeysel yorumunuz',openLoopEye:'Açık kalan kritik soru',offerEye:'Yorumunuzu seçin',offerHead:'Ücretsiz yorumunuzun açık bıraktığı soruyu yanıtlayın.',offerBody:'İki derinlikten birini seçin. Kesin sorunuz ve yedi kartınız yorumla birlikte güvenle devam eder.',contextAttached:'Soru ve yedi kart bağlı kalır',exploreCards:'Yedi kartın tümünü sırayla inceleyin',levelsAria:'İki yorum derinliğinden birini seçin'},
    ptbr:{surfaceEye:'Sua leitura superficial gratuita',openLoopEye:'A pergunta crítica que continua aberta',offerEye:'Escolha sua leitura',offerHead:'Responda à pergunta que sua leitura gratuita deixa em aberto.',offerBody:'Escolha uma de duas profundidades. Sua pergunta exata e as sete cartas continuam com segurança na leitura.',contextAttached:'Pergunta e sete cartas permanecem vinculadas',exploreCards:'Explore as sete cartas em ordem',levelsAria:'Escolha entre duas profundidades de leitura'},
    pt:{surfaceEye:'A sua leitura superficial gratuita',openLoopEye:'A pergunta crítica que continua em aberto',offerEye:'Escolha a sua leitura',offerHead:'Responda à pergunta que a sua leitura gratuita deixa em aberto.',offerBody:'Escolha uma de duas profundidades. A sua pergunta exata e as sete cartas continuam em segurança com a leitura.',contextAttached:'Pergunta e sete cartas permanecem ligadas',exploreCards:'Explore as sete cartas por ordem',levelsAria:'Escolha entre duas profundidades de leitura'}
  };
  Object.keys(TWO_TIER_STATIC_COPY).forEach(function (lang) {
    if (lang === 'pt' && !STATIC_COPY.pt) STATIC_COPY.pt = Object.assign({}, STATIC_COPY.ptbr);
    Object.assign(STATIC_COPY[lang] || (STATIC_COPY[lang] = {}), TWO_TIER_STATIC_COPY[lang]);
  });
  var QUESTION_ERROR_COPY = {
    en: 'Enter at least 8 characters so your reading has a clear question.',
    es: 'Escribe al menos 8 caracteres para que la lectura tenga una pregunta clara.',
    de: 'Gib mindestens 8 Zeichen ein, damit deine Deutung eine klare Frage hat.',
    tr: 'Yorumun net bir soruya dayanması için en az 8 karakter yazın.',
    ptbr: 'Escreva pelo menos 8 caracteres para que a leitura tenha uma pergunta clara.',
    pt: 'Escreva pelo menos 8 caracteres para que a leitura tenha uma pergunta clara.'
  };
  Object.keys(QUESTION_ERROR_COPY).forEach(function (lang) {
    Object.assign(STATIC_COPY[lang] || (STATIC_COPY[lang] = {}), { questionError: QUESTION_ERROR_COPY[lang] });
  });
  function applyStaticCopy() {
    var currentLang = localeContext().lang;
    var dict = STATIC_COPY[currentLang] || (currentLang === 'pt' ? STATIC_COPY.ptbr : null);
    if (!dict) return;
    document.querySelectorAll('[data-sc7-copy]').forEach(function (el) {
      var value = dict[el.getAttribute('data-sc7-copy')];
      if (value != null) el.textContent = value;
    });
    document.querySelectorAll('[data-sc7-aria]').forEach(function (el) {
      var value = dict[el.getAttribute('data-sc7-aria')];
      if (value != null) el.setAttribute('aria-label', value);
    });
  }
  Object.assign(COPY.en, {"deliveryLong":"AI-assisted; most readings arrive within 90 minutes, and on busy days it can take up to 24 hours.","deliveryShort":"Usually within 90 minutes; rarely up to 24 hours","deckErrorText":"Your cards could not be loaded. Your question is saved. Check your connection and try again.","retryDeck":"Try loading my cards again","runtimeError":"Secure checkout could not be loaded. Your reading is saved. Tap retry to reconnect."});
  Object.assign(COPY.tr, {"deliveryLong":"Yorumlar yapay zekâ desteğiyle hazırlanır; çoğu 90 dakika içinde gelir, yoğun günlerde 24 saate kadar sürebilir.","deliveryShort":"Genellikle 90 dakika; nadiren 24 saate kadar","deckErrorText":"Kartlar yüklenemedi. Sorunuz kaydedildi. Bağlantınızı kontrol edip yeniden deneyin.","retryDeck":"Kartlarımı yeniden yükle","runtimeError":"Güvenli ödeme yüklenemedi. Açılımınız kaydedildi. Bağlantıyı yeniden kurmak için tekrar deneyin."});
  Object.assign(COPY.es, {"deliveryLong":"Preparadas con IA: la mayoría de las lecturas llegan en 90 minutos; en días de mucha demanda pueden tardar hasta 24 horas.","deliveryShort":"Normalmente en 90 minutos; excepcionalmente hasta 24 horas","deckErrorText":"No se pudieron cargar tus cartas. Tu pregunta está guardada. Comprueba la conexión e inténtalo de nuevo.","retryDeck":"Volver a cargar mis cartas","runtimeError":"No se pudo cargar el pago seguro. Tu lectura está guardada. Reintenta para conectar."});
  Object.assign(COPY.de, {"deliveryLong":"Mit KI erstellt: Die meisten Deutungen kommen innerhalb von 90 Minuten; an vollen Tagen kann es bis zu 24 Stunden dauern.","deliveryShort":"Meist innerhalb von 90 Minuten; selten bis zu 24 Stunden","deckErrorText":"Deine Karten konnten nicht geladen werden. Deine Frage ist gespeichert. Prüfe deine Verbindung und versuche es erneut.","retryDeck":"Meine Karten erneut laden","runtimeError":"Der sichere Checkout konnte nicht geladen werden. Deine Legung ist gespeichert. Versuche die Verbindung erneut."});
  Object.assign(COPY.ptbr, {"deliveryLong":"Preparadas com IA: a maioria das leituras chega em até 90 minutos; em dias de muita demanda pode levar até 24 horas.","deliveryShort":"Normalmente em até 90 minutos; raramente até 24 horas","deckErrorText":"Não foi possível carregar suas cartas. Sua pergunta está salva. Verifique a conexão e tente novamente.","retryDeck":"Carregar minhas cartas novamente","runtimeError":"Não foi possível carregar o pagamento seguro. Sua leitura está salva. Tente conectar novamente."});

  function deliveryLong() { return copy('deliveryLong'); }
  function deliveryShort() { return copy('deliveryShort'); }

  function placeholderFor(intent) {
    if (localeContext().lang === 'es') {
      if (!intent) return '¿Qué debería comprender sobre esta situación y mi siguiente paso?';
      if (intent === 'Love & connection') return '¿Qué debería comprender sobre el momento actual de esta conexión?';
      if (intent === 'Career') return '¿Qué puede pasar si sigo en este puesto o cambio de rumbo?';
      if (intent === 'Personal growth') return '¿Qué patrón se repite y qué podría interrumpirlo?';
      return '¿Qué puede pasar si sigo este camino y qué debo considerar antes de decidir?';
    }
    if (localeContext().lang === 'de') {
      if (!intent) return 'Was sollte ich über diese Situation und meinen nächsten Schritt verstehen?';
      if (intent === 'Love & connection') return 'Was sollte ich über den aktuellen Stand dieser Verbindung verstehen?';
      if (intent === 'Career') return 'Was kann geschehen, wenn ich bleibe oder den Kurs ändere?';
      if (intent === 'Personal growth') return 'Welches Muster wiederholt sich und was könnte es unterbrechen?';
      return 'Was kann geschehen, wenn ich diesen Weg fortsetze, und was sollte ich vor der Entscheidung bedenken?';
    }
    if (localeContext().lang === 'ptbr') {
      if (!intent) return 'O que devo entender sobre esta situação e meu próximo passo?';
      if (intent === 'Love & connection') return 'O que devo entender sobre o momento atual desta conexão?';
      if (intent === 'Career') return 'O que pode acontecer se eu ficar neste trabalho e se eu mudar?';
      if (intent === 'Personal growth') return 'Que padrão continua se repetindo e como posso interrompê-lo?';
      return 'O que pode acontecer se eu continuar neste caminho e o que devo considerar antes de decidir?';
    }
    if (localeContext().lang === 'tr') {
      if (!intent) return 'Bu durum ve sonraki adımım hakkında neyi anlamalıyım?';
      if (intent === 'Love & connection') return 'Bu bağın şu anki durumuyla ilgili neyi anlamalıyım?';
      if (intent === 'Career') return 'Bu rolde kalırsam ve yön değiştirirsem ne olabilir?';
      if (intent === 'Personal growth') return 'Hangi örüntü tekrarlanıyor ve bunu ne kesebilir?';
      return 'Bu yolda devam edersem ne olabilir ve karar vermeden önce neyi düşünmeliyim?';
    }
    if (!intent) return 'What should I understand about this situation and my next step?';
    if (intent === 'Love & connection') return 'What should I understand about this connection?';
    if (intent === 'Career') return 'What should I understand about this opportunity?';
    if (intent === 'Personal growth') return 'What pattern am I ready to change?';
    return 'What should I consider before deciding?';
  }
  function twoPathsPromise(intent) {
    if (localeContext().lang === 'ptbr') {
      if (intent === 'Love & connection') return 'Compare o que as cartas sugerem se você se aproximar, esperar, estabelecer um limite ou mantiver o padrão atual.';
      if (intent === 'Career') return 'Compare o caminho de ficar, aceitar, esperar ou mudar de direção.';
      if (intent === 'Personal growth') return 'Entenda o que se repete, o que reforça o padrão e o que pode interrompê-lo.';
      return 'Veja aonde o padrão atual pode levar e o que pode mudar se você agir.';
    }
    if (localeContext().lang === 'tr') {
      if (intent === 'Love & connection') return 'Yaklaşmanız, beklemeniz, sınır koymanız veya mevcut örüntüyü sürdürmeniz halinde kartların gösterdiği yolları karşılaştırın.';
      if (intent === 'Career') return 'Kalma, kabul etme, bekleme veya yön değiştirme yollarını karşılaştırın.';
      if (intent === 'Personal growth') return 'Neyin tekrarlandığını, örüntüyü neyin güçlendirdiğini ve neyin kesebileceğini anlayın.';
      return 'Mevcut örüntünün nereye gittiğini ve harekete geçerseniz neyin değişebileceğini görün.';
    }
    if (intent === 'Love & connection') return 'See what the spread suggests if you reach out, wait, set a boundary or continue the current pattern.';
    if (intent === 'Career') return 'Compare the path of staying, accepting, waiting or changing direction.';
    if (intent === 'Personal growth') return 'Understand what keeps repeating, what reinforces it and what could interrupt the pattern.';
    return 'See where the current pattern leads and what may change if you act.';
  }

  function normalizedQuestion(value) {
    return String(value || '').toLocaleLowerCase(localeContext().locale || undefined)
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[’']/g, ' ').replace(/\s+/g, ' ').trim()
      .replace(/^[¿¡!?.,;:\s]+/, '').trim();
  }

  function questionRule(value) {
    var q = normalizedQuestion(value);
    var lang = localeContext().lang;
    if (!q) return null;
    var plan = /^(?:how|what (?:should|can|do)|wie|was (?:soll|kann)|como|o que (?:devo|posso)|como|que (?:debo|puedo)|nas[iı]l|ne yapmal[iı]|ne yapabil)/.test(q)
      || /(?:next step|what to do|nächste[nr]? schritt|was tun|proximo passo|que hacer|sonraki ad[iı]m|ne yapmal[iı]|ne yapmam)/.test(q);
    if (plan) return { index: 2, key: 'plan' };
    var compareWord = lang === 'de' ? /\b(?:oder|gegenuber|zwischen)\b/.test(q)
      : lang === 'es' ? /\b(?:o|versus|entre)\b/.test(q)
      : (lang === 'pt' || lang === 'ptbr') ? /\b(?:ou|versus|entre)\b/.test(q)
      : lang === 'tr' ? /\b(?:veya|yoksa|ya da)\b/.test(q)
      : /\b(?:or|versus|vs|between)\b/.test(q);
    var compare = compareWord
      || /(?:stay.+(?:leave|change)|leave.+stay|bleiben.+(?:gehen|wechseln)|quedarme.+(?:irme|cambiar)|ficar.+(?:sair|mudar)|kal.+(?:git|degis)|bekle.+(?:hareket|yaz)|hareket.+bekle)/.test(q);
    if (compare) return { index: 1, key: 'compare' };
    var yesNo = /^(?:should|will|is|are|can|do|does|did|would|could|soll|wird|ist|sind|kann|debo|sera|es|puedo|deveria|vai|e|posso|m[iı]y[iı]m|mi|olacak|olur|yapmal[iı])/.test(q);
    return yesNo ? { index: 0, key: 'essential' } : null;
  }

  function interpolate(template, values) {
    return String(template || '').replace(/\{([a-zA-Z0-9_]+)\}/g, function (_, key) {
      return values && values[key] != null ? String(values[key]) : '';
    });
  }
  function pricingTier(tier) {
    return (s.pricing.tiers || []).find(function (item) { return item.tier === tier; }) || null;
  }
  function tierPrice(index) {
    var quote = pricingTier(RAIL_TIERS[index]);
    if (s.pricing.status === 'ready' && quote && quote.available && quote.formatted_price) return quote.formatted_price;
    if (s.pricing.status === 'ready') return copy('priceError');
    return s.pricing.status === 'error' ? copy('priceError') : copy('priceLoading');
  }
  function selectedPriceReady() {
    var quote = pricingTier(RAIL_TIERS[s.pkg]);
    return s.pricing.status === 'ready' && !!(quote && quote.available && quote.variant_id && quote.formatted_price);
  }
  function selectedPriceUnavailable() { return s.pricing.status === 'ready' && !selectedPriceReady(); }
  function purchaseCtaLabel(name, price, unavailable) {
    if (s.mode === 'example') return copy('startReading');
    if (s.pricing.status === 'error') return copy('retryPrice');
    if (unavailable) return copy('packageUnavailable');
    if (s.checkoutBusy) return copy('openingCheckout');
    return selectedPriceReady() ? copy('chooseTier') + name + ' — ' + price : copy('priceLoading');
  }

  function cardImg(name, width) {
    var requestedWidth = Math.max(120, Math.min(420, Number(width) || 420));
    return window.DeckauraTarotArt ? window.DeckauraTarotArt.url(name, requestedWidth) : '';
  }
  function shortName(name) {
    if (SHORTS[name]) return SHORTS[name];
    return name.replace(/^The\s+/, '').length > 16 ? name.replace(/^The\s+/, '') : name;
  }
  function liveCardLine(card, reversed) {
    return window.DeckauraFreeCompact ? window.DeckauraFreeCompact.theme({card:card.name},localeContext().lang==='ptbr'?'pt':localeContext().lang) : '';
  }
  function freeCompactContract() {
    var language=localeContext().lang;
    var cards=s.mode==='example'||s.cards.length!==7 ? cardData().map(function(item){return {card:item.canonicalName,displayName:item.name,aliases:[item.canonicalName,item.name],orientation:item.reversed?'Reversed':'Upright'};}) : s.cards.map(function(item){var name=localizedCardName(item.card.name);return {card:item.card.name,displayName:name,aliases:[item.card.name,name],orientation:item.reversed?'Reversed':'Upright'};});
    return {kind:'seven',locale:language==='ptbr'?'pt':language,question:s.question,cards:cards};
  }
  function freeCompactSummary() {
    var helper=window.DeckauraFreeCompact;if(!helper)return '';
    var contract=freeCompactContract(),server=s.server;
    var trusted=server&&/^(ready|fallback)$/.test(String(server.status||''))&&server.compactTrusted===true&&server.compactLanguage===canonicalPreviewLanguage(localeContext().lang);
    return trusted&&helper.validated(server.compactInsight,contract)||helper.seven(contract);
  }
  function activeCardFrame(card) {
    if (!card) return '';
    var name = String(card.name || '').replace(/^The\s+/, '').toLowerCase();
    if (['death', 'tower', 'devil', 'ten of swords'].indexOf(name) < 0) return '';
    return copy('activeFrame');
  }
  function cardData() {
    if (s.mode === 'example' || (s.phase === 'intro' && !s.cards.length)) {
      var fullDeck = deck() || [];
      return EXAMPLE_CARDS.map(function (example, index) {
        var source = fullDeck.find(function (item) { return item.name === example.name; });
        return {
          label: (copy('positions') || DISPLAY)[index],
          name: localizedCardName(example.name), canonicalName: example.name,
          short: shortName(localizedCardName(example.name)),
          line: source ? liveCardLine(source, example.reversed === true) : example.line,
          reversed: example.reversed === true
        };
      });
    }
    return s.cards.map(function (item, index) {
      return {
        label: (copy('positions') || DISPLAY)[index],
        name: localizedCardName(item.card.name),
        canonicalName: item.card.name,
        short: shortName(localizedCardName(item.card.name)),
        line: liveCardLine(item.card, item.reversed),
        reversed: item.reversed === true
      };
    });
  }

  /* ------------ compute (design renderVals port) ------------ */
  function computeVals() {
    var isIntro = s.phase === 'intro';
    var isReveal = s.phase === 'reveal';
    var isResult = s.phase === 'result';
    var started = s.phase !== 'intro';
    var all = s.revealed >= 7;
    var data = cardData();
    var len = s.question.length;
    var questionReady = s.question.trim().length >= MIN_QUESTION_LENGTH;
    var qs = (s.question.match(/\?/g) || []).length;

    var cards = data.map(function (c, i) {
      var revealed = i < s.revealed;
      var isActive = i === s.active && revealed;
      var rot = -15 + i * 5;
      var lift = LIFTS[i];
      return {
        n: '0' + (i + 1), label: c.label, name: c.name, short: c.short, line: c.line, reversed: !!c.reversed,
        revealed: revealed, faceDown: !revealed,
        img: cardImg(c.canonicalName || c.name),
        artTf: c.reversed ? 'rotate(180deg)' : 'none',
        tf: 'rotate(' + rot + 'deg) translateY(' + (isActive ? lift - 18 : lift) + 'px)' + (isActive ? ' scale(1.07)' : ''),
        z: isActive ? 20 : 10 - Math.abs(3 - i),
        numColor: isActive ? '#EBD6A6' : 'rgba(180,174,187,0.65)',
        faceBorder: isActive ? 'rgba(200,169,107,0.95)' : 'rgba(200,169,107,0.45)',
        dotBg: revealed ? '#C8A96B' : 'transparent',
        aria: revealed ? 'Card ' + (i + 1) + ': ' + c.label + ', ' + c.name + ', ' + (c.reversed ? 'reversed' : 'upright') : 'Card ' + (i + 1) + ', face down. Open it.'
      };
    });
    var previewCards = data.map(function (c, i) {
      return {
        tf: 'rotate(' + (-15 + i * 5) + 'deg) translateY(' + LIFTS[i] + 'px)',
        img: cardImg(c.canonicalName || c.name, 180),
        artTf: c.reversed ? 'rotate(180deg)' : 'none'
      };
    });
    var activeCard = s.active >= 0 && s.active < s.revealed ? data[s.active] : null;

    var intentIds = ['Love & connection', 'A decision', 'Career', 'Personal growth'];
    var intentLabels = copy('intents') || intentIds;
    var intents = intentIds.map(function (label, intentIndex) {
      var on = s.intent === label;
      return {
        label: intentLabels[intentIndex] || label, on: on,
        tick: on ? '1' : '0',
        bg: on ? '#E9D8B7' : '#FFFDF8',
        border: on ? '#9B6B2F' : '#CDBB9D',
        color: on ? '#3D2B16' : '#4F4339'
      };
    });

    var tierNames = copy('tierNames') || COPY.en.tierNames;
    var offerGuide = window.DeckauraReadingOfferGuide;
    if (offerGuide && typeof offerGuide.packageName === 'function') tierNames = RAIL_TIERS.map(function (tier, index) { return offerGuide.packageName(tier) || tierNames[index]; });
    var packageCopy = copy('packages') || COPY.en.packages;
    var recommendationRule = { index: 0, key: 'focused' };
    var defs = packageCopy.map(function (item, index) {
      return {
        name: tierNames[index], checkoutName: tierNames[index], price: tierPrice(index),
        badge: item.badge, lengthNote: item.length, promise: item.promise,
        reason: '',
        bullets: item.bullets.slice()
      };
    });

    var localizedNeeds = copy('needs') || NEED_LABELS;
    var needs = NEED_LABELS.map(function (label, i) {
      var on = s.need === i;
      return {
        label: localizedNeeds[i] || label, on: on,
        bg: on ? '#FFFFFF' : 'transparent',
        border: on ? '#C8A96B' : '#DED5C4',
        borderWidth: on ? '2px' : '1px',
        dotBorder: on ? '#C8A96B' : '#C4BAA6',
        dot: on ? '#C8A96B' : 'transparent'
      };
    });

    var packages = defs.map(function (p, i) {
      var on = s.pkg === i;
      var quote = pricingTier(RAIL_TIERS[i]);
      var unavailable = s.pricing.status === 'ready' && !(quote && quote.available && quote.variant_id && quote.formatted_price);
      var recommendedByQuestion = i === 0;
      return {
        name: p.name, checkoutName: p.checkoutName, price: p.price, promise: p.promise, lengthNote: p.lengthNote,
        badge: (recommendedByQuestion ? copy('recommended') : p.badge).toUpperCase(),
        badgeColor: recommendedByQuestion ? '#8A6E33' : '#6E5B32',
        bullets: p.bullets.map(function (t) { return { t: t }; }),
        on: on,
        bg: on ? '#FFFFFF' : '#EFE9DC',
        border: on ? '#C8A96B' : '#D8CDB8',
        borderWidth: on ? '2px' : '1px',
        dotBorder: on ? '#C8A96B' : '#C4BAA6',
        dotFill: on ? '#C8A96B' : 'transparent',
        tick: on ? '✓' : '',
        selText: on ? copy('selected') : '',
        selColor: '#8A6E33',
        tabIndex: on ? '0' : '-1',
        unavailable: unavailable,
        ctaOff: s.checkoutBusy || unavailable,
        ctaLabel: purchaseCtaLabel(p.name, p.price, unavailable)
      };
    });

    /* The recommendation starts from the guidance choice, but the primary
       purchase card must follow an explicit package selection. Keeping these
       separate prevents the CTA label/price from disagreeing with checkout. */
    var rec = defs[s.pkg] || defs[0];
    var recIsRecommended = s.pkg === 0;
    var faqIds = ['contents', 'difference', 'reviewer', 'delivery', 'email', 'others', 'reversed', 'changes', 'subscription', 'refund', 'privacy'];
    var faqDefs = (copy('faq') || FAQ_DEFS).map(function (f, index) {
      return { id: faqIds[index], q: f[0], a: f[1] };
    }).filter(function (f) { return f.id !== 'reviewer'; });
    var faqs = faqDefs.map(function (f, i) {
      return {
        id: f.id, q: f.q, a: f.id === 'delivery' ? deliveryLong() : f.id === 'difference' ? copy('paidDifferenceAnswer') : f.a,
        open: s.faq === i,
        iconTf: s.faq === i ? 'rotate(45deg)' : 'none'
      };
    });
    var positionDefs = copy('positionInfo') || POSITIONS_INFO;
    var positions = positionDefs.map(function (p, i) {
      return { num: p[0], label: p[1], desc: p[2], tf: 'translateY(' + [30, 12, 2, -4, 2, 12, 30][i] + 'px)' };
    });
    var sampleDefs = copy('samples') || SAMPLES;
    var samples = sampleDefs.map(function (q) { return { q: q }; });
    var hintText = copy('hintOne');
    var hintColor = '#E6E1EC';
    if (len > 0 && qs > 1) { hintText = copy('hintMany'); hintColor = '#F4B4BC'; }
    else if (len >= 24) { hintText = copy('hintFocused'); hintColor = '#7FD1A8'; }

    var questionEcho = s.mode === 'example' ? sampleDefs[0] : (s.question.trim() || sampleDefs[0]);
    var pastCard = data[0], presentCard = data[1], hiddenCard = data[2], obstacleCard = data[3], externalCard = data[4], adviceCard = data[5], outcomeCard = data[6];
    function overviewCard(card) {
      var meaning = card ? String(card.line || '').replace(/[.!?]+$/, '') : '';
      return {
        name: card ? card.name + (card.reversed ? ' (' + copy('reversed') + ')' : '') : '',
        meaning: localeContext().lang === 'de' ? meaning : meaning.toLocaleLowerCase(localeContext().locale || undefined)
      };
    }
    var op = overviewCard(pastCard), orp = overviewCard(presentCard), oh = overviewCard(hiddenCard), oo = overviewCard(obstacleCard), oe = overviewCard(externalCard), oa = overviewCard(adviceCard), out = overviewCard(outcomeCard);
    var arcValues = { question: questionEcho, pastName: op.name, pastMeaning: op.meaning, presentName: orp.name, presentMeaning: orp.meaning, hiddenName: oh.name, hiddenMeaning: oh.meaning, obstacleName: oo.name, obstacleMeaning: oo.meaning, externalName: oe.name, externalMeaning: oe.meaning, adviceName: oa.name, adviceMeaning: oa.meaning, outcomeName: out.name, outcomeMeaning: out.meaning };
    var teaserLead = interpolate(copy('surfacePattern'), arcValues);
    var teaserHeadline = copy('freeHeading');
    var teaserBridge = copy('freeBridge');
    var teaserCta = s.mode === 'example' ? copy('startReading') : copy('teaserCta');
    var surfaceSummary = freeCompactSummary();
    var surfaceDetail = '';
    var openLoopQuestion = interpolate(copy('surfaceOpenLoop'), arcValues);
    var insights = [{
      n: '01', label: copy('visibleLabel'), lead: copy('visibleLead'), body: teaserLead
    }];
    var isPreparing = s.phase === 'preparing';
    var introPrimary = isIntro && !s.wide ? document.querySelector('[data-rv2-act="draw"]') : null;
    var introPrimaryVisible = false;
    if (introPrimary) {
      var introPrimaryRect = introPrimary.getBoundingClientRect();
      introPrimaryVisible = introPrimaryRect.bottom > 0 && introPrimaryRect.top < window.innerHeight;
    }
    var mobileLabel = copy('step3');
    var mobileValue = rec.name + ' · ' + rec.price;
    var mobileCta = copy('viewOptions');
    if (isIntro) {
      mobileLabel = questionReady ? copy('step1Ready') : copy('step1Ask');
      mobileValue = questionReady ? copy('questionReady') : copy('writeQuestion');
      mobileCta = questionReady ? copy('drawCards') : copy('writeQuestion');
    } else if (isPreparing) {
      mobileLabel = copy('step2Preparing');
      mobileValue = copy(s.deckStatus === 'error' ? 'deckErrorText' : 'arranging');
      mobileCta = copy(s.deckStatus === 'error' ? 'retryDeck' : 'preparing');
    } else if (isReveal) {
      mobileLabel = interpolate(copy('step2Card'), { n: s.revealed });
      mobileValue = all ? copy('allOpen') : s.revealed + ' / 7';
      mobileCta = copy('showOverview');
    } else if (s.mode === 'example') {
      mobileLabel = copy('sampleStep');
      mobileValue = copy('sampleFlow');
      mobileCta = copy('startMine');
    }

    return {
      isIntro: isIntro, isReveal: isReveal, isResult: isResult, started: started,
      wide: s.wide, notWide: !s.wide,
      showSteps: started && !!s.xwide,
      barSpacer: !s.wide || isResult,
      spreadCols: s.wide ? 'repeat(7,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))',
      preparing: s.phase === 'preparing',
      deckLoading: s.phase === 'preparing' && s.deckStatus !== 'error',
      deckError: s.deckStatus === 'error',
      deckErrorText: copy('deckErrorText'), retryDeck: copy('retryDeck'),
      canReveal: s.cards.length === 7 && s.deckStatus !== 'error',
      isExample: s.mode === 'example',
      showTeaser: isResult,
      question: s.question, questionEcho: questionEcho,
      placeholder: placeholderFor(s.intent),
      charCount: len + ' / 240',
      hintText: hintText, hintColor: hintColor, qError: s.qError,
      composerBorder: s.qError ? 'rgba(217,134,134,0.6)' : (s.focused || len > 0 ? 'rgba(200,169,107,0.55)' : 'rgba(200,169,107,0.2)'),
      tableHint: len > 0 ? copy('tableReady') : copy('tableWrite'),
      previewOpacity: len > 0 ? '1' : '0.78',
      drawLabel: s.revealed >= 7 ? copy('drawAgain') : copy('drawCards'),
      hasSpread: isIntro && s.revealed >= 7,
      canSkip: !all,
      stageActionLabel: all ? copy('showOverview') : copy('openNext') + ' (' + Math.min(7, s.revealed + 1) + '/7)',
      stageSecondaryLabel: copy('revealAll'),
      revealedCount: s.revealed,
      progressShort: s.revealed + '/7',
      progressPercent: Math.round((s.revealed / 7) * 100) + '%',
      progressText: interpolate(copy('progress'), { n: s.revealed }),
      progressLabel: interpolate(copy('step2Card'), { n: s.revealed }).replace(/^.*?·\s*/, ''),
      reversedLabel: copy('reversed'),
      stepCardsColor: isReveal ? '#EBD6A6' : '#B4AEBB',
      stepResultColor: isResult ? '#EBD6A6' : '#5E5F6B',
      activeLabel: activeCard ? activeCard.label : copy('horseshoe'),
      activeName: activeCard ? activeCard.name : copy('activeEmpty'),
      activeLine: activeCard ? activeCard.line : copy('activeEmptyLine'),
      activeFrame: activeCardFrame(activeCard),
      showActiveFrame: !!activeCardFrame(activeCard),
      activeReversed: activeCard ? !!activeCard.reversed : false,
      activeArtTf: activeCard && activeCard.reversed ? 'rotate(180deg)' : 'none',
      hasActiveCard: !!activeCard,
      activeImg: activeCard ? cardImg(activeCard.canonicalName || activeCard.name) : '',
      liveMessage: s.live,
      teaserLead: teaserLead,
      surfaceSummary: surfaceSummary,
      surfaceDetail: surfaceDetail,
      showSurfaceDetail: !!surfaceDetail,
      openLoopQuestion: openLoopQuestion,
      teaserHeadline: teaserHeadline,
      teaserBridge: teaserBridge,
      teaserCta: teaserCta,
      showResultNotice: !!(s.server && (/^(limited|error)$/.test(String(s.server.status || '')) || s.server.quotaLimited === true)),
      resultNotice: s.server && (s.server.status === 'limited' || s.server.quotaLimited === true) ? copy('resultLimit') : copy('checkoutFallback'),
      cards: cards, previewCards: previewCards, intents: intents, needs: needs,
      /* sonuç bölümleri (satırlar + akordeon) reveal bitmeden gerçek kartları
         SIZDIRMASIN: result fazına kadar örnek set gösterilir */
      cards3: (isResult ? cards : EXAMPLE_CARDS.map(function (c, i) {
        return {
          n: '0' + (i + 1), label: c.label, name: c.name, short: c.short, line: c.line,
          reversed: !!c.reversed, revealed: true, faceDown: false,
          img: cardImg(c.name), artTf: c.reversed ? 'rotate(180deg)' : 'none'
        };
      })),
      highlightCards: (isResult ? cards.slice(5, 7) : []),
      get cards4() { return this.cards3.slice(0, 7); },
      packages: packages, faqs: faqs, positions: positions, samples: samples,
      insights: insights, locked: LOCKED,
      recEyebrow: recIsRecommended ? copy('recommended') : copy('selectedLevel'),
      recName: rec.name, recCheckoutName: rec.checkoutName, recPrice: rec.price, recPromise: rec.promise,
      recReason: recIsRecommended ? rec.reason : (recommendationRule ? copy('selectedReason') : ''),
      showRecReason: recIsRecommended || !!recommendationRule,
      recCtaLabel: purchaseCtaLabel(rec.name, rec.price, selectedPriceUnavailable()),
      recBullets: rec.bullets.map(function (t) { return { t: t }; }),
      compareOpen: s.compare,
      compareLabel: s.compare ? copy('compareHide') : copy('compareShow'),
      cardsOpen: s.cardsOpen,
      cardsIconTf: s.cardsOpen ? 'rotate(45deg)' : 'none',
      showUpgrade: true,
      deliveryLine: deliveryLong(),
      deliveryShort: deliveryShort(),
      quotaNote: copy('quotaNote'),
      finalHeading: isIntro || (isResult && s.mode === 'example') ? copy('finalExampleHeading') : copy('finalHeading'),
      finalBody: isIntro || (isResult && s.mode === 'example') ? copy('finalExampleBody') : copy('finalBody'),
      finalCta: isIntro || (isResult && s.mode === 'example') ? copy('startReading') : copy('finalCta'),
      mobileBar: !s.wide && isResult && !s.inPkg,
      mobileLabel: mobileLabel,
      mobileValue: mobileValue,
      mobileCta: mobileCta,
      pricingStatus: s.pricing.status,
      pricingMessage: selectedPriceUnavailable() ? copy('packageUnavailable') : (s.pricing.status === 'ready' ? copy('pricingReady') : (s.pricing.status === 'error' ? copy('retryPrice') : copy('pricingLoading'))),
      checkoutOff: s.mode === 'live' && (s.checkoutBusy || selectedPriceUnavailable()), checkoutBusy: String(s.checkoutBusy)
    };
  }

  /* ------------ renderer (shared data-rv2 core) ------------ */
  function resolvePath(vals, item, path) {
    if (item != null && typeof item !== 'object' && path.indexOf('.') === -1) return item;
    if (item && path.indexOf('.') > -1) {
      var key = path.split('.').slice(1).join('.');
      var v = item;
      key.split('.').forEach(function (p) { v = v == null ? v : v[p]; });
      if (v !== undefined) return v;
    }
    var out = vals;
    path.split('.').forEach(function (p) { out = out == null ? out : out[p]; });
    return out;
  }
  function applyBindings(scope, vals, item) {
    function skip(el) { return !item && el.closest('[data-rv2-item]'); }
    scope.querySelectorAll('[data-rv2-bind]').forEach(function (el) {
      if (skip(el)) return;
      var v = resolvePath(vals, item, el.getAttribute('data-rv2-bind'));
      el.textContent = v == null ? '' : String(v);
    });
    scope.querySelectorAll('*').forEach(function (el) {
      if (skip(el)) return;
      Array.prototype.slice.call(el.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-rv2-attr-') !== 0) return;
        var real = attr.name.slice('data-rv2-attr-'.length);
        var value = attr.value.replace(/%%([\w.]+)%%/g, function (_, key) {
          var v = resolvePath(vals, item, key);
          return v == null ? '' : String(v);
        });
        if (real === 'value') { if (el.value !== value) el.value = value; }
        else if (real === 'disabled') {
          var disabled = value === 'true' || value === '1' || value === 'disabled';
          el.disabled = disabled;
          if (disabled) el.setAttribute('disabled', '');
          else el.removeAttribute('disabled');
        } else if (real === 'src' && !value) el.removeAttribute('src');
        else el.setAttribute(real, value);
      });
    });
    scope.querySelectorAll('[data-rv2-ifitem]').forEach(function (el) {
      if (skip(el)) return;
      var key = el.getAttribute('data-rv2-ifitem');
      var neg = key.charAt(0) === '!';
      var v = resolvePath(vals, item, neg ? key.slice(1) : key);
      el.style.display = (neg ? !v : !!v) ? 'contents' : 'none';
    });
    if (item != null) {
      scope.querySelectorAll('[data-rv2-list]').forEach(function (h) {
        var nm = h.getAttribute('data-rv2-list');
        if (nm.indexOf('.') === -1) return;
        var sub = resolvePath(vals, item, nm.replace(/\d+$/, ''));
        if (sub && sub.length) instantiateList(h, sub, vals);
      });
    }
  }
  var TPL = {};
  document.querySelectorAll('template[data-rv2-tpl]').forEach(function (t) {
    TPL[t.getAttribute('data-rv2-tpl')] = t;
  });
  function instantiateList(host, items, vals) {
    var name = host.getAttribute('data-rv2-list').split('.').pop();
    var tpl = TPL[name] || TPL[name.replace(/\d+$/, '')];
    if (!tpl) return;
    host.style.display = 'contents';
    while (host.firstChild) host.removeChild(host.firstChild);
    items.forEach(function (item, idx) {
      var node = tpl.content.cloneNode(true);
      var holder = document.createElement('div');
      holder.style.display = 'contents';
      holder.setAttribute('data-rv2-item', name.replace(/\d+$/, ''));
      holder.setAttribute('data-rv2-idx', String(idx));
      holder.appendChild(node);
      applyBindings(holder, vals, item);
      host.appendChild(holder);
    });
  }
  function render() {
    var vals = computeVals();
    document.querySelectorAll('[data-rv2-if]').forEach(function (el) {
      if (el.closest('[data-rv2-item]')) return;
      var key = el.getAttribute('data-rv2-if');
      var neg = key.charAt(0) === '!';
      var v = vals[neg ? key.slice(1) : key];
      el.style.display = (neg ? !v : !!v) ? 'contents' : 'none';
    });
    document.querySelectorAll('[data-rv2-list]').forEach(function (host) {
      if (host.closest('[data-rv2-item]')) return;
      var name = host.getAttribute('data-rv2-list');
      instantiateList(host, vals[name] || vals[name.replace(/\d+$/, '')] || [], vals);
    });
    applyBindings(document, vals, null);
    applyStaticCopy();
    var q = document.querySelector('textarea[data-rv2-question]');
    if (q) {
      if (q.value !== s.question) q.value = s.question;
      q.setAttribute('aria-invalid', s.qError ? 'true' : 'false');
      q.setAttribute('aria-describedby', s.qError ? 'sc7-question-hint sc7-question-error' : 'sc7-question-hint');
    }
    /* Some theme/app click delegates stop package-card events before they
       reach our document listener. Bind the recreated comparison cards at the
       target so every visible radio behaves like a real selection control. */
    document.querySelectorAll('[data-rv2-item="packages"] [role="radio"]').forEach(function (control) {
      if (control.getAttribute('data-sc7-package-bound') === '1') return;
      control.setAttribute('data-sc7-package-bound', '1');
      control.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        var holder = control.closest('[data-rv2-item="packages"]');
        selectPackage(holder ? Number(holder.getAttribute('data-rv2-idx')) : -1, true);
      });
      control.addEventListener('keydown', function (ev) {
        var holder = control.closest('[data-rv2-item="packages"]');
        var current = holder ? Number(holder.getAttribute('data-rv2-idx')) : -1;
        var next = current;
        if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') next = (current + 1) % RAIL_TIERS.length;
        else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') next = (current - 1 + RAIL_TIERS.length) % RAIL_TIERS.length;
        else if (ev.key === 'Home') next = 0;
        else if (ev.key === 'End') next = RAIL_TIERS.length - 1;
        else if (ev.key !== 'Enter' && ev.key !== ' ') return;
        ev.preventDefault();
        selectPackage(next, true);
      });
    });
    /* The live theme can intercept delegated button clicks. Keep the primary
       draw action bound directly after every render, just like package radios. */
    document.querySelectorAll('[data-rv2-act="draw"]').forEach(function (button) {
      if (button.getAttribute('data-sc7-draw-bound') === '1') return;
      button.setAttribute('data-sc7-draw-bound', '1');
      button.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        draw();
      });
    });
    if (document.body) {
      document.body.classList.toggle('sc7-has-sticky-bar', vals.mobileBar);
      document.body.classList.toggle('sc7-flow-active', s.phase !== 'intro');
      document.body.classList.toggle('sc7-result-open', s.phase === 'result');
    }
    window.requestAnimationFrame(syncPackageVisibility);
    if (packageFocusIndex >= 0) focusPackageControl(packageFocusIndex);
    observeFunnelVisibility();
    persistState();
  }

  /* ------------ helpers ------------ */
  function activeTab() { return !document.hidden && document.visibilityState !== 'hidden'; }
  function scheduleVisibilityEvent(el, name) {
    var key = (s.attemptId || 'page') + ':' + name;
    if (seenMilestones[key] || visibilityTimers[key]) return;
    visibilityTimers[key] = window.setTimeout(function check() {
      if (!el.isConnected || !el.__sc7HalfVisible) { delete visibilityTimers[key]; return; }
      if (!activeTab() || (name === 'free_overview_viewed' && (!s.server || s.server.status === 'loading'))) {
        visibilityTimers[key] = window.setTimeout(check, 250);
        return;
      }
      delete visibilityTimers[key];
      if (publishFunnelEvent(name, { intent: s.intent }, { once: true })) seenMilestones[key] = true;
    }, 1000);
  }
  function observeFunnelVisibility() {
    if (!('IntersectionObserver' in window)) return;
    if (!milestoneObserver) {
      milestoneObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.__sc7HalfVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          var name = entry.target.getAttribute('data-sc7-view-event');
          if (entry.target.hasAttribute('data-sc7-engagement')) engagedVisible = entry.target.__sc7HalfVisible;
          if (entry.target.__sc7HalfVisible && name) scheduleVisibilityEvent(entry.target, name);
        });
      }, { threshold: [0, 0.5, 1] });
      engagedLastTick = Date.now();
      engagedTimer = window.setInterval(function () {
        var now = Date.now();
        if (engagedVisible && activeTab() && s.attemptId && s.server && s.server.status !== 'loading') {
          engagedMs += Math.min(1000, now - engagedLastTick);
          if (engagedMs >= 10000) publishFunnelEvent('free_overview_engaged', { intent: s.intent }, { once: true });
        }
        engagedLastTick = now;
      }, 500);
    }
    document.querySelectorAll('[data-sc7-view-event], [data-sc7-engagement]').forEach(function (el) {
      if (!el.__sc7Observed) { el.__sc7Observed = true; milestoneObserver.observe(el); }
    });
  }
  function clearPriceWatchdog() {
    if (!priceWatchdog) return;
    window.clearTimeout(priceWatchdog);
    priceWatchdog = 0;
  }
  function armPriceWatchdog() {
    if (priceWatchdog || s.pricing.status !== 'loading') return;
    var startedAt = Date.now();
    priceWatchdog = window.setTimeout(function () {
      priceWatchdog = 0;
      if (s.pricing.status !== 'loading') return;
      applyPricingSnapshot({ status: 'error', error_code: 'price_timeout', latency_ms: Date.now() - startedAt, tiers: [] });
      checkoutStatus(copy('priceError') + ' — ' + copy('retryPrice'), true);
    }, 8000);
  }
  function applyPricingSnapshot(snapshot) {
    if (!snapshot || !/^(loading|ready|error)$/.test(String(snapshot.status || ''))) return;
    var tiers = Array.isArray(snapshot.tiers) ? snapshot.tiers.map(function (item) {
      item = item || {};
      var tier = cleanToken(item.tier, 20);
      return {
        tier: RAIL_TIERS.indexOf(tier) >= 0 ? tier : '',
        variant_id: /^\d{10,20}$/.test(String(item.variant_id || '')) ? String(item.variant_id) : '',
        price_minor: Number.isInteger(Number(item.price_minor)) && Number(item.price_minor) > 0 ? Number(item.price_minor) : null,
        formatted_price: String(item.formatted_price || '').replace(/[<>]/g, '').slice(0, 40),
        available: item.available === true
      };
    }).filter(function (item) { return item.tier; }) : [];
    s.pricing = {
      status: String(snapshot.status),
      currency: /^[A-Z]{3}$/.test(String(snapshot.currency || '')) ? String(snapshot.currency) : '',
      latency_ms: Math.max(0, Number(snapshot.latency_ms) || 0),
      error_code: cleanToken(snapshot.error_code || '', 80),
      tiers: tiers
    };
    if (s.pricing.status === 'loading') armPriceWatchdog();
    else clearPriceWatchdog();
    if (s.pricing.status === 'ready' && !s.checkoutBusy) checkoutStatus('', false);
    if (s.pricing.status === 'loading') publishFunnelEvent('price_check_started', { status: 'loading' }, { once: true });
    else if (s.pricing.status === 'ready') publishFunnelEvent('price_check_succeeded', { status: 'ready', currency: s.pricing.currency, latency_ms: s.pricing.latency_ms }, { once: true });
    else publishFunnelEvent('price_check_failed', { status: 'error', error_code: s.pricing.error_code || 'price_unavailable', latency_ms: s.pricing.latency_ms }, { once: true });
    render();
  }
  function retryPricing() {
    if (s.pricing.status === 'loading') return false;
    var readingId = s.readingId;
    s.pricing = { status: 'loading', currency: '', latency_ms: 0, error_code: '', tiers: [] };
    publishFunnelEvent('price_check_retried', { status: 'loading' });
    checkoutStatus(copy('priceRetrying'), false);
    armPriceWatchdog(); render();
    loadCheckoutRuntime(true).then(function () {
      if (s.readingId !== readingId || s.mode !== 'live') return;
      var api = window.DeckauraDeepReading;
      if (!enableRail() || !api || typeof api.retryPricing !== 'function') throw new Error('pricing_api_unavailable');
      return api.retryPricing();
    }).catch(function (error) {
      if (s.readingId !== readingId) return;
      applyPricingSnapshot({ status: 'error', error_code: error.message || 'pricing_api_unavailable', tiers: [] });
      checkoutStatus(copy('runtimeError'), true);
    });
    return true;
  }

  function normalizeFreePreviewLimit(value) {
    var reason = String(value && value.reason || '').trim().toLowerCase();
    var nextAt = Number(value && value.nextAt);
    var now = Date.now();
    if (!FREE_PREVIEW_LIMIT_REASONS[reason] || !Number.isFinite(nextAt)
      || nextAt <= now || nextAt > now + FREE_PREVIEW_LIMIT_MAX_FUTURE) return null;
    return { v: 1, reason: reason, nextAt: Math.floor(nextAt) };
  }
  function readFreePreviewLimit() {
    try {
      var raw = localStorage.getItem(FREE_PREVIEW_LIMIT_KEY);
      if (!raw) return null;
      var limit = normalizeFreePreviewLimit(JSON.parse(raw));
      if (!limit) localStorage.removeItem(FREE_PREVIEW_LIMIT_KEY);
      return limit;
    } catch (_) {
      try { localStorage.removeItem(FREE_PREVIEW_LIMIT_KEY); } catch (_cleanup) {}
      return null;
    }
  }
  function rememberFreePreviewLimit(value) {
    var freeReadings = value && value.freeReadings && typeof value.freeReadings === 'object' ? value.freeReadings : {};
    var limit = normalizeFreePreviewLimit({
      reason: value && value.reason,
      nextAt: value && value.nextAt != null ? value.nextAt : freeReadings.nextAt
    });
    if (!limit) return null;
    try { localStorage.setItem(FREE_PREVIEW_LIMIT_KEY, JSON.stringify(limit)); } catch (_) {}
    return limit;
  }
  function clearFreePreviewLimit() {
    try { localStorage.removeItem(FREE_PREVIEW_LIMIT_KEY); } catch (_) {}
  }
  function applyFreePreviewLimit(limit) {
    s.previewToken = '';
    s.server = { status: 'limited', answer: '', insights: [], compactInsight: '', previewToken: '' };
    s.freeReadings = { remaining: 0, nextAt: limit.nextAt, reason: limit.reason };
  }
  function visitorId() {
    var key = 'deckaura_visitor_v1';
    try {
      var existing = localStorage.getItem(key);
      if (existing && /^[a-zA-Z0-9_-]{16,96}$/.test(existing)) return existing;
      var bytes = new Uint8Array(18); crypto.getRandomValues(bytes);
      var next = 'v_' + Array.prototype.map.call(bytes, function (x) { return x.toString(16).padStart(2, '0'); }).join('');
      localStorage.setItem(key, next);
      return next;
    } catch (_) { return 'v_' + Date.now().toString(16) + Math.random().toString(36).slice(2, 10); }
  }
  function scrollToId(id, offset) {
    try {
      var el = document.getElementById(id);
      if (!el) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      var top = el.getBoundingClientRect().top + window.pageYOffset - (offset || 92);
      window.scrollTo({ top: top, behavior: 'smooth' });
    } catch (_) {}
  }
  function cardsString() {
    return s.cards.map(function (item, index) {
      return CANONICAL[index] + ': ' + item.card.name + ' ' + (item.reversed ? 'Reversed' : 'Upright');
    }).join('; ');
  }
  function pickSeven() {
    var d = deck();
    if (!d) return [];
    var ids = [];
    d.forEach(function (_, i) { ids.push(i); });
    for (var i = ids.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = ids[i]; ids[i] = ids[j]; ids[j] = tmp;
    }
    return ids.slice(0, 7).map(function (id) { return { card: d[id], reversed: Math.random() < 0.3 }; });
  }

  function cleanPreviewText(value, maxLength) {
    var text = String(value == null ? '' : value)
      .replace(/<[^>]*>/g, ' ')
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var max = Math.max(80, Number(maxLength) || 280);
    if (text.length <= max) return text;
    var shortened = text.slice(0, max + 1);
    var sentence = Math.max(shortened.lastIndexOf('. '), shortened.lastIndexOf('? '), shortened.lastIndexOf('! '));
    if (sentence >= Math.floor(max * 0.55)) return shortened.slice(0, sentence + 1).trim();
    var word = shortened.lastIndexOf(' ');
    return shortened.slice(0, word > Math.floor(max * 0.65) ? word : max).trim() + '…';
  }

  function normalizePreviewInsights(value) {
    if (!Array.isArray(value)) return [];
    return value.slice(0, 2).map(function (item) {
      if (typeof item === 'string') return { lead: '', body: cleanPreviewText(item, 160) };
      if (!item || typeof item !== 'object') return { lead: '', body: '' };
      return {
        lead: cleanPreviewText(item.lead || item.title || item.label, 90),
        body: cleanPreviewText(item.body || item.text || item.description, 160)
      };
    }).filter(function (item) { return !!(item.lead || item.body); });
  }
  function canonicalPreviewLanguage(value) {
    var lang = String(value || '').toLowerCase().replace(/_/g, '-');
    if (lang === 'ptbr') return 'pt';
    lang = lang.split('-')[0];
    return /^(en|de|es|tr|pt)$/.test(lang) ? lang : '';
  }
  function trustedCompactPreview(data, language) {
    if (!data || data.presentationVariant !== PRESENTATION_VARIANT || data.languageVerified !== true
      || !/^(passed|passed_fallback)$/.test(String(data.compactInsightAuditStatus || ''))
      || !canonicalPreviewLanguage(language) || canonicalPreviewLanguage(data.resolvedLanguage) !== canonicalPreviewLanguage(language)) return '';
    return cleanPreviewText(data.compactInsight, 900);
  }

  /* ------------ server + rail ------------ */
  function fetchFreeReading() {
    if (s.busy || (s.server && s.server.status !== 'loading') || s.mode !== 'live' || s.cards.length !== 7) return;
    var cachedLimit = readFreePreviewLimit();
    if (cachedLimit) {
      applyFreePreviewLimit(cachedLimit);
      publishFunnelEvent('free_overview_requested', { intent: s.intent, card_count: 7 }, { once: true });
      publishFunnelEvent('free_overview_resolved', { result_mode: 'limit', reason: 'rolling_24h', source: 'client_quota_cache', latency_ms: 0 }, { once: true });
      enableRailWhenReady();
      render();
      if (s.checkoutBusy) continueQueuedCheckout();
      return;
    }
    s.busy = true;
    s.server = { status: 'loading', answer: '', insights: [], compactInsight: '', previewToken: '' };
    var startedAt = Date.now();
    var locale = localeContext();
    publishFunnelEvent('free_overview_requested', { intent: s.intent, card_count: 7 }, { once: true });
    var body = {
      visitorId: visitorId(),
      question: s.question,
      type: 'Tarot',
      context: CONTEXT,
      lang: locale.lang === 'ptbr' ? 'pt' : locale.lang,
      tool: PAGE,
      signals: cardsString(),
      cards: cardsString(),
      spread: 'seven-card-horseshoe',
      scope: SCOPE,
      confidence: CONFIDENCE,
      focus: s.intent || '',
      readingId: s.readingId,
      curiosityQuestion: '',
      snapshotVersion: 'reading-snapshot-v2',
      funnelVersion: FUNNEL_VERSION,
      presentationVariant: PRESENTATION_VARIANT,
      locale: locale.locale, country: locale.country, currency: locale.currency, market: locale.country
    };
    var requestId = ++freeRequestId;
    function isCurrent() {
      return requestId === freeRequestId && s.mode === 'live' && s.readingId === body.readingId
        && s.question === body.question && cardsString() === body.cards && localeContext().locale === body.locale;
    }
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    freeRequestController = ac;
    var timer = ac ? setTimeout(function () { ac.abort(); }, 16000) : 0;
    fetch(API_BASE + '/free-reading', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body), signal: ac ? ac.signal : undefined
    }).then(function (r) {
      var response = { ok: r.ok, status: r.status, data: null };
      return r.json().then(function (j) { response.data = j; return response; }, function () { return response; });
    })
      .then(function (res) {
        if (!isCurrent()) return;
        var dataObject = res.data && typeof res.data === 'object' && !Array.isArray(res.data);
        var answer = dataObject ? cleanPreviewText(res.data.answer, 280) : '';
        var responseInsights = dataObject ? normalizePreviewInsights(res.data.insights) : [];
        var compactInsight = dataObject ? cleanPreviewText(res.data.compactInsight, 900) : '';
        var responseToken = String(dataObject && (res.data.token || res.data.previewToken) || '');
        responseToken = res.ok && /^[a-f0-9]{32}$/i.test(responseToken) ? responseToken : '';
        var limited = dataObject && res.data.freeReadings && Number(res.data.freeReadings.remaining) === 0 && !answer && !compactInsight && !responseInsights.length;
        if (res.ok && dataObject && (answer || compactInsight || responseInsights.length || limited)) {
          var served = String(res.data.servedSource || res.data.source || 'api');
          var quotaLimited = res.data.quotaFallback === true || res.data.freeValueStatus === 'deterministic_quota_fallback';
          var trustedCompact = trustedCompactPreview(res.data, locale.lang);
          s.server = {
            status: limited ? 'limited' : (quotaLimited || /fallback/i.test(served) ? 'fallback' : 'ready'),
            answer: answer,
            insights: responseInsights,
            compactInsight: compactInsight,
            compactTrusted: !!trustedCompact,
            compactLanguage: trustedCompact ? canonicalPreviewLanguage(locale.lang) : '',
            quotaLimited: quotaLimited,
            previewToken: responseToken
          };
          s.previewToken = s.server.previewToken;
          s.freeReadings = res.data.freeReadings || null;
          if (!limited) clearFreePreviewLimit();
          publishFunnelEvent('free_overview_resolved', { result_mode: limited || quotaLimited ? 'limit' : (s.server.status === 'fallback' ? 'fallback' : 'success'), source: served, latency_ms: Date.now() - startedAt }, { once: true });
          if (res.data.quotaFallback === true) publishFunnelEvent('seven_card_quota_fallback_served', { result_mode: 'fallback', source: served }, { once: true });
        } else {
          var reason = String(res.data && (res.data.reason || res.data.code || res.data.error) || '');
          var responseLimited = res.status === 429 || /free_limit|rate_limit|limit/i.test(reason) || (res.data && res.data.freeReadings && Number(res.data.freeReadings.remaining) === 0);
          var validation = res.status === 400 || res.status === 422 || /validation|invalid|question|required/i.test(reason);
          var strictLimit = res.status === 429 ? rememberFreePreviewLimit(res.data) : null;
          s.server = { status: responseLimited ? 'limited' : 'error', answer: '', insights: [], compactInsight: '', previewToken: responseToken };
          s.previewToken = responseToken;
          s.freeReadings = res.data && res.data.freeReadings || (strictLimit ? { remaining: 0, nextAt: strictLimit.nextAt, reason: strictLimit.reason } : null);
          publishFunnelEvent('free_overview_resolved', { result_mode: responseLimited ? 'limit' : (validation ? 'validation_error' : 'server_error'), reason: responseLimited ? 'rolling_24h' : (validation ? 'validation_error' : 'server_response'), latency_ms: Date.now() - startedAt }, { once: true });
        }
      })
      .catch(function (error) {
        if (!isCurrent()) return;
        var mode = error && error.name === 'AbortError' ? 'timeout' : 'network_error';
        s.server = { status: 'error', answer: '', insights: [], compactInsight: '', previewToken: '' };
        publishFunnelEvent('free_overview_resolved', { result_mode: mode, reason: mode, latency_ms: Date.now() - startedAt }, { once: true });
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
        if (!isCurrent()) return;
        freeRequestController = null;
        s.busy = false;
        enableRail();
        render();
        if (s.checkoutBusy) {
          continueQueuedCheckout();
        }
        /* liste yeniden kurulumları yüksekliği oynatabiliyor; sonuç + CTA
           birlikte görünür kalsın diye çapayı yeniden sabitle */
        // A late overview must not pull someone away from the package they are reading.
      });
  }
  function enableRail() {
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.enable !== 'function' || s.cards.length !== 7) return false;
    if (s.railReadingId === s.readingId && s.railPreviewToken === s.previewToken) {
      try { if (typeof api.setPreviewAuthorization === 'function') api.setPreviewAuthorization({ previewToken: s.previewToken, transportFallback: !s.previewToken }); } catch (_) {}
      try { if (typeof api.setCheckoutPending === 'function') api.setCheckoutPending(false); } catch (_) {}
      try { if (typeof api.getPricingSnapshot === 'function') applyPricingSnapshot(api.getPricingSnapshot()); } catch (_) {}
      return true;
    }
    var railReadingId = s.readingId;
    var locale = localeContext();
    try {
      api.enable({
        type: 'Tarot',
        question: s.question,
        context: CONTEXT,
        tool: PAGE,
        canonicalPage: PAGE,
        signals: cardsString(),
        cards: cardsString(),
        spread: 'seven-card-horseshoe',
        scope: SCOPE,
        confidence: CONFIDENCE,
        focus: s.intent || '',
        readingId: s.readingId,
        visitorId: visitorId(),
        curiosityQuestion: '',
        previewToken: s.previewToken,
        transportFallback: !s.previewToken,
        checkoutPending: false,
        defaultTier: 'deeper',
        teaser: false,
        gate: true,
        lang: locale.locale,
        presentationVariant: PRESENTATION_VARIANT,
        checkoutRecoveryHost: document.querySelector('[data-sc7-cart-review]'),
        onCartReviewReady: cartReviewReady,
        checkoutProperties: {
          '_presentation_variant': PRESENTATION_VARIANT,
          '_analytics_schema': ANALYTICS_SCHEMA,
          '_funnel_version': FUNNEL_VERSION,
          '_attempt_id': s.attemptId,
          '_reading_id': s.readingId,
          '_Focus': s.intent || ''
        },
        checkoutGuard: function (detail) {
          var selectedQuote = pricingTier(String(detail && detail.tier || ''));
          return s.mode === 'live' && s.phase === 'result' && s.readingId === railReadingId && s.pricing.status === 'ready' && !!(selectedQuote && selectedQuote.available && selectedQuote.variant_id === String(detail && detail.variantId || ''));
        },
        onPricingStateChange: function (snapshot) { if (s.readingId === railReadingId) applyPricingSnapshot(snapshot); },
        onPriceReady: function () {
          if (s.pricing.status !== 'ready' || !s.checkoutQueued || s.railReadingId !== railReadingId || s.readingId !== railReadingId) return;
          window.setTimeout(continueQueuedCheckout, 0);
        }
      });
      if (typeof api.setPreviewAuthorization === 'function') {
        api.setPreviewAuthorization({ previewToken: s.previewToken, transportFallback: !s.previewToken });
      }
      if (typeof api.setCheckoutPending === 'function') api.setCheckoutPending(false);
      s.railReadingId = railReadingId;
      s.railPreviewToken = s.previewToken;
      return true;
    } catch (_) { return false; }
  }
  function resetRailReadyRetry() {
    if (railReadyTimer) window.clearTimeout(railReadyTimer);
    railReadyTimer = 0;
    railReadyAttempts = 0;
  }
  function enableRailWhenReady() {
    if (enableRail()) { resetRailReadyRetry(); return true; }
    if (s.mode !== 'live' || s.cards.length !== 7) return false;
    var readingId = s.readingId;
    if (s.pricing.status === 'idle') applyPricingSnapshot({ status: 'loading', tiers: [] });
    loadCheckoutRuntime(false).then(function () {
      if (s.readingId === readingId && s.mode === 'live') enableRail();
    }).catch(function (error) {
      if (s.readingId !== readingId) return;
      applyPricingSnapshot({ status: 'error', error_code: error.message || 'runtime_unavailable', tiers: [] });
      checkoutStatus(copy('runtimeError'), true);
    });
    return false;
  }

  function checkoutStatus(message, isError) {
    if (isError && checkoutCartReviewPending()) { message = ''; isError = false; }
    var st = document.querySelector('[data-rv2-checkout-status]');
    if (!st) return;
    st.textContent = message || '';
    st.style.display = message ? 'block' : 'none';
    st.style.color = isError ? '#F4B4BC' : '#E6E1EC';
  }
  function clearCartReview() {
    s.checkoutCartReview = null;
    var host = document.querySelector('[data-sc7-cart-review]');
    if (!host) return;
    host.__deckauraRecoveryGeneration = (host.__deckauraRecoveryGeneration || 0) + 1;
    host.textContent = '';
    host.hidden = true;
  }
  function checkoutCartReviewReason(reason) {
    return /^(reading_cart_conflict|cart_changed|cart_operation_busy|cart_result_uncertain|cart_read_failed|cart_add_failed|checkout_guard_unavailable|checkout_navigation_busy)$/.test(String(reason || ''));
  }
  function checkoutCartReviewPending() {
    var review = s.checkoutCartReview;
    return !!(review && checkoutCartReviewReason(review.reason) && s.phase === 'result' && s.mode === 'live' && review.readingId === s.readingId && s.railReadingId === s.readingId && document.querySelector('[data-sc7-cart-review]'));
  }
  function cartReviewReady(host) {
    if (!host || s.phase !== 'result' || s.mode !== 'live') return;
    var readingId = s.readingId, generation = host.__deckauraRecoveryGeneration;
    if (host.__sc7FocusedRecoveryGeneration === generation) return;
    host.__sc7FocusedRecoveryGeneration = generation;
    window.requestAnimationFrame(function () {
      if (s.phase !== 'result' || s.mode !== 'live' || s.readingId !== readingId || host.hidden || host.isConnected === false || host.__deckauraRecoveryGeneration !== generation) return;
      host.setAttribute('tabindex', '-1');
      var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try { host.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' }); } catch (_) {}
      try { host.focus({ preventScroll: true }); } catch (_) {}
    });
  }
  function clearCheckoutWatchdog() {
    if (!checkoutWatchdog) return;
    window.clearTimeout(checkoutWatchdog);
    checkoutWatchdog = 0;
  }
  function armCheckoutWatchdog() {
    clearCheckoutWatchdog();
    checkoutWatchdog = window.setTimeout(function () {
      checkoutWatchdog = 0;
      if (!s.checkoutBusy) return;
      publishFunnelEvent('checkout_failed', { tier: RAIL_TIERS[s.pkg], reason: 'prepare_timeout' });
      resetCheckout(copy('checkoutRetry'), true);
    }, 10000);
  }
  function resetCheckout(message, isError) {
    clearCheckoutWatchdog();
    s.checkoutQueued = false;
    s.checkoutVia = '';
    s.checkoutBusy = false;
    render();
    checkoutStatus(message || '', isError === true);
  }
  function retryAuthorization(via) {
    if (s.busy) {
      checkoutStatus(copy('preparingReading'), false);
      return;
    }
    s.server = null;
    s.previewToken = '';
    s.railPreviewToken = '';
    s.checkoutBusy = true;
    s.checkoutQueued = true;
    s.checkoutVia = via || 'recommendation';
    publishFunnelEvent('reading_link_retried', { tier: RAIL_TIERS[s.pkg], via: via || 'recommendation' });
    checkoutStatus(copy('preparingReading'), false);
    render();
    armCheckoutWatchdog();
    fetchFreeReading();
  }
  function railCheckout(via) {
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.checkoutNow !== 'function') return false;
    if (!enableRail()) return false;
    var desiredTier = RAIL_TIERS[s.pkg] || 'deeper';
    if (!selectedPriceReady()) {
      if (s.pricing.status === 'error') return false;
      s.checkoutQueued = true;
      s.checkoutVia = via || 'recommendation';
      publishFunnelEvent('checkout_price_waiting', { tier: desiredTier, via: s.checkoutVia });
      return null;
    }
    try { api.setTier(desiredTier, 'user'); } catch (_) { return false; }
    try {
      if (typeof api.getSelectedTier === 'function' && api.getSelectedTier() !== desiredTier) return false;
      if (typeof api.isPriceReady === 'function' && !api.isPriceReady()) {
        s.checkoutQueued = true;
        s.checkoutVia = via || 'recommendation';
        publishFunnelEvent('checkout_price_waiting', { tier: desiredTier, via: s.checkoutVia });
        return null;
      }
      return api.checkoutNow() === true;
    } catch (_) { return false; }
  }
  function continueQueuedCheckout() {
    if (!s.checkoutQueued || !s.checkoutBusy) return;
    var via = s.checkoutVia || 'recommendation';
    s.checkoutQueued = false;
    var started = railCheckout(via + '_ready');
    if (started === null) {
      s.checkoutQueued = true;
      checkoutStatus(copy('priceConfirming'), false);
      return;
    }
    if (!started) {
      resetCheckout(copy('packageFailed'), true);
      return;
    }
    checkoutStatus(copy('checkoutOpening'), false);
  }
  function startCheckout(via) {
    if (s.checkoutBusy) return;
    clearCartReview();
    ensureAttempt('checkout_cta');
    publishFunnelEvent('checkout_cta_clicked', { tier: RAIL_TIERS[s.pkg], via: via || 'recommendation' });
    if (s.phase !== 'result' || s.mode !== 'live' || s.cards.length !== 7) {
      checkoutStatus(copy('readingIncomplete'), true);
      publishFunnelEvent('checkout_blocked', { reason: 'reading_incomplete', via: via || 'recommendation' });
      return;
    }
    if (s.pricing.status === 'error') { retryPricing(); return; }
    if (!selectedPriceReady()) {
      checkoutStatus(copy('priceRetrying'), false);
      retryPricing();
      return;
    }
    s.checkoutBusy = true;
    s.checkoutVia = via || 'recommendation';
    render();
    armCheckoutWatchdog();
    var started = railCheckout(s.checkoutVia);
    if (started === null) checkoutStatus(copy('priceConfirming'), false);
    else if (!started) resetCheckout(copy('checkoutRetry'), true);
    else checkoutStatus(copy('checkoutOpening'), false);
  }

  /* ------------ flow ------------ */
  var prepTimer = 0;
  function focusComposer() {
    var q = document.querySelector('textarea[data-rv2-question]');
    if (!q) return;
    try {
      q.scrollIntoView({ block: 'center', behavior: 'smooth' });
      q.focus({ preventScroll: true });
    } catch (_) { try { q.focus(); } catch (_) {} }
  }
  function focusFaqControl(idx) {
    window.requestAnimationFrame(function () {
      var control = document.querySelector('[data-rv2-item="faqs"][data-rv2-idx="' + idx + '"] button');
      if (!control) return;
      try { control.focus({ preventScroll: true }); } catch (_) { try { control.focus(); } catch (_error) {} }
    });
  }
  function scrollPhaseBlock(cond, offset) {
    /* faz bloğunu (display:contents sarmalayıcının İLK gerçek çocuğunu)
       görünür alana getir; tasarımın "en üste kaydır" davranışı uzun
       sayfada kartları viewport dışında bırakıyordu */
    var els = document.querySelectorAll('[data-rv2-if="' + cond + '"]');
    for (var i = 0; i < els.length; i += 1) {
      if (els[i].style.display === 'none') continue;
      var child = els[i].firstElementChild;
      if (!child) continue;
      var r = child.getBoundingClientRect();
      if (r.height < 40) continue;
      try {
        window.scrollTo({ top: r.top + window.pageYOffset - (offset || 84), behavior: 'smooth' });
      } catch (_) {}
      return true;
    }
    return false;
  }
  function loadDeckForDraw(retry) {
    if (s.deckStatus === 'loading') return;
    var attempt = ++deckAttemptId;
    var question = s.question;
    s.mode = 'live'; s.phase = 'preparing'; s.deckStatus = 'loading'; s.qError = false;
    s.cards = []; s.revealed = 0; s.active = -1;
    persistState(); render(); scrollPhaseBlock('preparing', 140);
    ensureDependency('deckaura-tarot-data.js', deck, retry).then(function () {
      if (attempt !== deckAttemptId || s.phase !== 'preparing' || s.question !== question) return;
      s.deckStatus = 'ready'; draw();
    }).catch(function () {
      if (attempt !== deckAttemptId || s.question !== question) return;
      s.deckStatus = 'error'; s.live = copy('deckErrorText'); render();
    });
  }
  function canUseSpread() { return s.mode === 'example' || s.cards.length === 7; }
  function draw() {
    if (s.deckStatus === 'loading') return;
    ensureAttempt('draw');
    if (s.question.trim().length < MIN_QUESTION_LENGTH) {
      s.qError = true;
      publishFunnelEvent('question_validation_failed', { reason: 'too_short' });
      render(); focusComposer(); return;
    }
    publishFunnelEvent('question_validated', { intent: s.intent }, { once: true });
    publishFunnelEvent('draw_submitted', { intent: s.intent, card_count: 7 }, { once: true });
    s.need = 0;
    s.pkg = 0;
    if (!deck()) { loadDeckForDraw(false); return; }
    clearCartReview();
    invalidateFreeRequest();
    s.deckStatus = 'ready';
    var picked = pickSeven();
    if (picked.length !== 7) { s.deckStatus = 'error'; s.phase = 'preparing'; render(); return; }
    s.cards = picked;
    /* secim/aclim gecikmesin: 7 on yuzu hemen on-yukle */
    try {
      picked.forEach(function (it) {
        var im = new Image();
        im.src = cardImg(it.card.name);
      });
    } catch (_) {}
    s.readingId = 'sc7-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    resetRailReadyRetry();
    clearPriceWatchdog();
    clearCheckoutWatchdog();
    s.server = null; s.previewToken = ''; s.railReadingId = ''; s.railPreviewToken = '';
    s.checkoutQueued = false; s.checkoutVia = ''; s.checkoutBusy = false;
    s.pricing = { status: 'idle', currency: '', latency_ms: 0, error_code: '', tiers: [] };
    s.phase = 'preparing'; s.qError = false; s.mode = 'live'; s.revealed = 0; s.active = -1;
    render();
    scrollPhaseBlock('preparing', 140);
    publishFunnelEvent('draw_succeeded', { intent: s.intent, card_count: 7 }, { once: true });
    enableRailWhenReady();
    if (prepTimer) clearTimeout(prepTimer);
    prepTimer = setTimeout(function () {
      prepTimer = 0;
      s.phase = 'reveal'; s.revealed = 0; s.active = -1;
      s.live = '';
      render();
      scrollPhaseBlock('isReveal', 84);
    }, 900);
  }
  function toResult() {
    if (!canUseSpread()) return;
    s.phase = 'result';
    render();
    scrollToId('reading');
    if (s.mode === 'live') {
      publishFunnelEvent('cards_completed', { card_count: 7, revealed_count: 7 }, { once: true });
      enableRail();
      fetchFreeReading();
    } else enableRail();
  }
  function revealNext() {
    if (!canUseSpread()) return;
    var n = s.revealed;
    if (n >= 7) { toResult(); return; }
    var d = cardData()[n];
    s.revealed = n + 1; s.active = n;
    s.live = interpolate(copy('cardRevealed'), { n: n + 1 }) + ' ' + d.label + ', ' + d.name + '.';
    if (s.revealed === 1) publishFunnelEvent('first_card_viewed', { revealed_count: 1 }, { once: true });
    if (s.revealed >= 7) publishFunnelEvent('cards_completed', { card_count: 7, revealed_count: 7 }, { once: true });
    render();
  }
  function revealAllToResult() {
    if (!canUseSpread()) return;
    if (prepTimer) { clearTimeout(prepTimer); prepTimer = 0; }
    s.revealed = 7; s.active = 6; s.live = copy('allRevealed');
    publishFunnelEvent('first_card_viewed', { revealed_count: 1 }, { once: true });
    publishFunnelEvent('cards_completed', { card_count: 7, revealed_count: 7 }, { once: true });
    toResult();
  }
  function finishPreparing() {
    if (!canUseSpread()) return;
    if (prepTimer) { clearTimeout(prepTimer); prepTimer = 0; }
    s.phase = 'reveal'; s.revealed = 0; s.active = -1; s.live = '';
    render(); scrollPhaseBlock('isReveal', 84);
  }
  function changeQuestion() {
    clearCartReview();
    invalidateFreeRequest();
    deckAttemptId += 1; s.deckStatus = 'idle';
    if (prepTimer) { clearTimeout(prepTimer); prepTimer = 0; }
    clearPriceWatchdog();
    clearCheckoutWatchdog();
    resetRailReadyRetry();
    try { if (window.DeckauraDeepReading && typeof window.DeckauraDeepReading.disable === 'function') window.DeckauraDeepReading.disable(); } catch (_) {}
    s.phase = 'intro'; s.mode = 'live'; s.revealed = 0; s.active = -1; s.cards = [];
    s.server = null; s.previewToken = ''; s.freeReadings = null; s.readingId = '';
    s.railReadingId = ''; s.railPreviewToken = ''; s.checkoutQueued = false; s.checkoutBusy = false;
    s.pricing = { status: 'idle', currency: '', latency_ms: 0, error_code: '', tiers: [] };
    s.attemptId = ''; s.sentEvents = {}; s.questionStarted = false; s.qError = false;
    engagedMs = 0; engagedVisible = false; seenMilestones = {}; visibilityTimers = {};
    render();
  }

  function focusPackageControl(idx) {
    window.requestAnimationFrame(function () {
      var control = document.querySelector('[data-rv2-item="packages"][data-rv2-idx="' + idx + '"] [role="radio"]');
      if (!control) return;
      try { control.focus({ preventScroll: true }); } catch (_) { try { control.focus(); } catch (_error) {} }
    });
  }

  function selectPackage(idx, focusAfter) {
    if (!Number.isInteger(idx) || idx < 0 || idx >= RAIL_TIERS.length) return;
    var quote = pricingTier(RAIL_TIERS[idx]);
    if (s.pricing.status === 'ready' && !(quote && quote.available)) return;
    if (s.pkg !== idx) clearCartReview();
    s.pkg = idx;
    publishFunnelEvent('package_selected', { tier: RAIL_TIERS[idx], via: 'package_card' });
    try {
      var api = window.DeckauraDeepReading;
      if (s.mode === 'live' && s.cards.length === 7 && api && api.setTier) api.setTier(RAIL_TIERS[idx], 'user');
    } catch (_) {}
    if (focusAfter) packageFocusIndex = idx;
    render();
    if (focusAfter) focusPackageControl(idx);
  }

  /* ------------ events ------------ */
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var scopeEl = t.closest('[data-sc7-scope]');
    var act = t.closest('[data-rv2-act]');
    if (act && scopeEl) {
      var a = act.getAttribute('data-rv2-act');
      if (a === 'draw') { ev.preventDefault(); draw(); }
      else if (a === 'retryDeck') { ev.preventDefault(); loadDeckForDraw(true); }
      else if (a === 'skipprepare') { ev.preventDefault(); finishPreparing(); }
      else if (a === 'skipresult') { ev.preventDefault(); revealAllToResult(); }
      else if (a === 'stage') { ev.preventDefault(); if (s.revealed >= 7) toResult(); else revealNext(); }
      else if (a === 'edit') { changeQuestion(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} window.setTimeout(focusComposer, 350); }
      else if (a === 'example') { changeQuestion(); s.phase = 'result'; s.mode = 'example'; s.revealed = 7; s.active = 6; render(); scrollToId('reading'); }
      else if (a === 'startown') { changeQuestion(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }
      else if (a === 'backreading') { s.phase = 'result'; render(); scrollToId('reading'); }
      else if (a === 'comparetoggle') { s.compare = !s.compare; render(); }
      else if (a === 'cardstoggle') { s.cardsOpen = !s.cardsOpen; render(); }
      else if (a === 'teasercta') {
        ev.preventDefault();
        publishFunnelEvent('offer_opened', { intent: s.intent, tier: RAIL_TIERS[s.pkg] }, { once: true });
        if (s.mode === 'example') { changeQuestion(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }
        else scrollToId('packages');
      }
      else if (a === 'finalcta') {
        ev.preventDefault();
        if (s.phase === 'result' && s.mode === 'example') { changeQuestion(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }
        else if (s.phase === 'result') scrollToId('packages');
        else { if (s.phase !== 'intro' || s.cards.length) changeQuestion(); else render(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }
      }
      else if (a === 'mobileact') {
        ev.preventDefault();
        if (s.phase === 'intro') draw();
        else if (s.phase === 'preparing') { if (s.deckStatus === 'error') loadDeckForDraw(true); }
        else if (s.phase === 'reveal') revealAllToResult();
        else if (s.mode === 'example') { changeQuestion(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }
        else scrollToId('packages');
      }
      else if (a === 'selectpackage') {
        ev.preventDefault();
        var selectionHolder = act.closest('[data-rv2-item="packages"]');
        selectPackage(selectionHolder ? Number(selectionHolder.getAttribute('data-rv2-idx')) : -1, true);
      }
      else if (a === 'getrec') {
        ev.preventDefault();
        if (s.mode === 'example') { changeQuestion(); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} return; }
        if (s.pricing.status === 'error') { retryPricing(); return; }
        startCheckout('recommendation');
      }
      else if (a === 'buypackage') {
        ev.preventDefault();
        ev.stopPropagation();
        var packageHolder = act.closest('[data-rv2-item="packages"]');
        var packageIndex = packageHolder ? Number(packageHolder.getAttribute('data-rv2-idx')) : -1;
        selectPackage(packageIndex);
        if (s.pricing.status === 'error') retryPricing();
        else startCheckout('package_card');
      }
      return;
    }
    var item = t.closest('[data-rv2-item]');
    if (!item || !scopeEl) return;
    var listName = item.getAttribute('data-rv2-item');
    var idx = Number(item.getAttribute('data-rv2-idx'));
    if (listName === 'cards') {
      if (s.phase !== 'reveal' && s.phase !== 'result') return;
      if (idx < s.revealed) { s.active = idx; render(); return; }
      /* kapali karta dokununca yalnizca SIRADAKI tek kart acilir */
      revealNext();
    }
    else if (listName === 'intents') {
      if (!Number.isInteger(idx) || idx < 0 || idx > 3) return;
      if (s.phase !== 'intro' || s.cards.length) changeQuestion();
      ensureAttempt('intent');
      s.intent = ['Love & connection', 'A decision', 'Career', 'Personal growth'][idx];
      publishFunnelEvent('intent_selected', { intent: s.intent });
      render();
    }
    else if (listName === 'needs') {
      var needQuote = pricingTier(RAIL_TIERS[idx]);
      if (s.pricing.status === 'ready' && !(needQuote && needQuote.available)) return;
      s.need = idx; s.pkg = idx;
      publishFunnelEvent('guidance_selected', { intent: String(idx), tier: RAIL_TIERS[idx] });
      publishFunnelEvent('package_selected', { tier: RAIL_TIERS[idx], via: 'guidance_selector' });
      try { if (window.DeckauraDeepReading && window.DeckauraDeepReading.setTier) window.DeckauraDeepReading.setTier(RAIL_TIERS[idx], 'user'); } catch (_) {}
      render();
    }
    else if (listName === 'packages') {
      if (t.closest('.sc7-package-details')) return;
      selectPackage(idx);
    }
    else if (listName === 'samples') {
      var sample = (copy('samples') || SAMPLES)[idx];
      if (typeof sample !== 'string') return;
      if (s.phase !== 'intro' || s.cards.length) changeQuestion();
      ensureAttempt('sample_question');
      s.question = sample; s.phase = 'intro'; s.qError = false; s.questionStarted = true;
      publishFunnelEvent('question_started', { intent: s.intent, source: 'sample' }, { once: true });
      render(); scrollToId('top', 110);
    }
    else if (listName === 'faqs') { s.faq = s.faq === idx ? -1 : idx; render(); focusFaqControl(idx); }
  });
  function syncComposerBorder(field) {
    var composer = field && field.closest('.sc7-composer');
    if (composer) composer.style.borderColor = s.qError ? 'rgba(217,134,134,0.6)' : s.focused || s.question.length ? 'rgba(200,169,107,0.55)' : 'rgba(200,169,107,0.2)';
  }
  function syncComposerHint(field) {
    var many = (s.question.match(/\?/g) || []).length > 1;
    var key = many ? 'hintMany' : s.question.length >= 24 ? 'hintFocused' : 'hintOne';
    document.querySelectorAll('[data-rv2-bind="hintText"]').forEach(function (el) { el.textContent = copy(key); });
    var hint = document.getElementById('sc7-question-hint');
    if (hint) hint.style.color = many ? '#F4B4BC' : s.question.length >= 24 ? '#7FD1A8' : '#E6E1EC';
    syncComposerBorder(field);
  }
  document.addEventListener('input', function (ev) {
    var t = ev.target;
    if (t && t.matches && t.matches('textarea[data-rv2-question]')) {
      if (s.phase !== 'intro') { t.value = s.question; return; }
      var nextQuestion = t.value.slice(0, 240);
      if (s.cards.length || s.readingId) { changeQuestion(); t.value = nextQuestion; }
      s.question = nextQuestion;
      s.qError = false;
      t.setAttribute('aria-invalid', 'false');
      t.setAttribute('aria-describedby', 'sc7-question-hint');
      if (s.question.trim()) {
        ensureAttempt('question');
        if (!s.questionStarted) {
          s.questionStarted = true;
          publishFunnelEvent('question_started', { intent: s.intent }, { once: true });
        }
      }
      var counter = document.querySelector('[data-rv2-bind="charCount"]');
      if (counter) counter.textContent = s.question.length + ' / 240';
      document.querySelectorAll('[data-rv2-if="qError"]').forEach(function (el) { el.style.display = 'none'; });
      var composer = t.closest('.sc7-composer');
      if (composer) { composer.setAttribute('data-error', 'false'); composer.style.borderColor = 'rgba(200,169,107,0.55)'; }
      if (s.phase === 'intro') {
        var ready = s.question.trim().length >= MIN_QUESTION_LENGTH;
        document.querySelectorAll('[data-rv2-bind="mobileLabel"]').forEach(function (el) { el.textContent = ready ? copy('step1Ready') : copy('step1Ask'); });
        document.querySelectorAll('[data-rv2-bind="mobileValue"]').forEach(function (el) { el.textContent = ready ? copy('questionReady') : copy('writeQuestion'); });
        document.querySelectorAll('[data-rv2-bind="mobileCta"]').forEach(function (el) { el.textContent = ready ? copy('drawCards') : copy('writeQuestion'); });
      }
      syncComposerHint(t);
      persistState();
    }
  });
  document.addEventListener('deckaura:funnel-event', function (ev) {
    var detail = ev && ev.detail || {};
    var name = detail.name || '';
    if (!detail.data || detail.data.tool !== PAGE || s.phase !== 'result' || s.mode !== 'live'
      || !s.readingId || s.railReadingId !== s.readingId) return;
    if (name === 'checkout_prepare_start') {
      if (s.checkoutBusy) {
        s.checkoutCartReview = null;
        s.checkoutBusy = true;
        s.checkoutQueued = false;
        clearCheckoutWatchdog();
        render();
      }
    } else if (name === 'checkout_cart_ready') {
      if (s.checkoutBusy) checkoutStatus(copy('checkoutOpening'), false);
      publishFunnelEvent('checkout_cart_verified', { tier: RAIL_TIERS[s.pkg] }, { once: true });
    } else if (name === 'checkout_redirect_success') {
      clearCheckoutWatchdog();
      publishFunnelEvent('checkout_started', { tier: RAIL_TIERS[s.pkg], via: s.checkoutVia || 'recommendation' }, { once: true });
    } else if (name === 'checkout_prepare_error') {
      var checkoutReason = String(detail.reason || (detail.data && (detail.data.reason || detail.data.status)) || 'prepare_error');
      s.checkoutCartReview = checkoutCartReviewReason(checkoutReason) && s.phase === 'result' && s.mode === 'live' && s.readingId && s.railReadingId === s.readingId ? { readingId: s.readingId, reason: checkoutReason } : null;
      publishFunnelEvent('checkout_failed', { tier: RAIL_TIERS[s.pkg], reason: checkoutReason });
      if (s.checkoutBusy) resetCheckout(copy('checkoutRetry'), true);
      if (checkoutCartReviewPending()) checkoutStatus('', false);
    }
  });
  document.addEventListener('focusin', function (ev) {
    var packageControl = ev.target && ev.target.closest ? ev.target.closest('[data-rv2-item="packages"] [role="radio"]') : null;
    if (packageControl) {
      var packageHolder = packageControl.closest('[data-rv2-item="packages"]');
      var packageIndex = packageHolder ? Number(packageHolder.getAttribute('data-rv2-idx')) : -1;
      packageFocusIndex = Number.isInteger(packageIndex) && packageIndex >= 0 && packageIndex < RAIL_TIERS.length ? packageIndex : -1;
    } else if (ev.target && ev.target !== document.body) {
      packageFocusIndex = -1;
    }
    if (ev.target && ev.target.matches && ev.target.matches('textarea[data-rv2-question]')) { s.focused = true; syncComposerHint(ev.target); }
  });
  document.addEventListener('focusout', function (ev) {
    if (ev.target && ev.target.matches && ev.target.matches('textarea[data-rv2-question]')) { s.focused = false; syncComposerBorder(ev.target); }
  });
  document.addEventListener('keydown', function (ev) {
    if (ev.target && ev.target.matches && ev.target.matches('textarea[data-rv2-question]')
      && ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); draw(); }
  });
  window.addEventListener('resize', function () {
    var wide = window.innerWidth > 900;
    var xwide = window.innerWidth > 1150;
    if (wide !== s.wide || xwide !== s.xwide) { s.wide = wide; s.xwide = xwide; render(); }
    else window.requestAnimationFrame(syncPackageVisibility);
  });
  var scrollTick = false;
  function applyBarVisibility() {
    var vis = ((!s.wide) || s.phase === 'result') && !s.inPkg;
    if (vis && s.phase === 'intro' && !s.wide) {
      var introPrimary = document.querySelector('[data-rv2-act="draw"]');
      if (introPrimary) {
        var introPrimaryRect = introPrimary.getBoundingClientRect();
        if (introPrimaryRect.bottom > 0 && introPrimaryRect.top < window.innerHeight) vis = false;
      }
    }
    document.querySelectorAll('[data-rv2-if="mobileBar"]').forEach(function (e) {
      e.style.display = vis ? 'contents' : 'none';
    });
    if (document.body) document.body.classList.toggle('sc7-has-sticky-bar', vis);
  }
  function syncPackageVisibility() {
    var el = s.phase === 'result' ? document.getElementById('packages') : null;
    var inPkg = false;
    if (el) {
      var r = el.getBoundingClientRect();
      inPkg = r.height > 0 && r.top < window.innerHeight - 96 && r.bottom > 96;
    }
    s.inPkg = inPkg;
    applyBarVisibility();
  }
  window.addEventListener('scroll', function () {
    if (scrollTick) return;
    scrollTick = true;
    requestAnimationFrame(function () {
      scrollTick = false;
      syncPackageVisibility();
    });
  }, { passive: true });

  window.addEventListener('pageshow', function (ev) {
    if (ev.persisted && s.checkoutBusy) {
      window.location.reload();
      return;
    }
    if (!ev.persisted && !s.checkoutBusy) return;
    clearCheckoutWatchdog();
    s.checkoutQueued = false; s.checkoutVia = ''; s.checkoutBusy = false;
    s.railReadingId = ''; s.railPreviewToken = '';
    render();
    if (s.phase === 'result' && s.mode === 'live') enableRail();
  });

  function boot() {
    s.sessionId = sessionId();
    var restored = restoreState();
    if (!s.sessionId) s.sessionId = sessionId();
    if (restored && s.phase !== 'intro' && !s.attemptId) s.attemptId = uuid('a_');
    render();
    if (restored && s.phase === 'result' && s.mode === 'live') {
      enableRailWhenReady();
      if (!s.previewToken) fetchFreeReading();
    }
    publishFunnelEvent('sc7_funnel_viewed', { source: restored ? 'restored' : 'new' });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
